import { useState, useRef, useCallback, useMemo, useEffect } from "react";
import LandingPage from "./LandingPage";
import { supabase } from "./supabaseClient";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend,
  ComposedChart, Area, Line, LineChart, ReferenceLine,
} from "recharts";
import {
  Upload, FileText, ChevronRight, ChevronLeft, Download, RotateCcw, AlertCircle, Check,
  ArrowUpDown, Search, TrendingUp, Calendar, CreditCard, Tag, Settings, Plus, Trash2,
  DollarSign, PiggyBank, BarChart3, Wallet, Building2, Target, Calculator,
  X, Edit3, Eye, EyeOff, LogOut,
  ArrowDown, ArrowUp, Minus, ChevronDown, LayoutDashboard,
  Landmark, TrendingDown, Briefcase, RefreshCw, Banknote, Menu,
  Image, ChevronUp, FileImage
} from "lucide-react";

/* ══════════════════════════════════════════════════════════════
   CONSTANTS & CATEGORIES
   ══════════════════════════════════════════════════════════════ */

const DEFAULT_EXPENSE_CATS = [
  { id: "alimentacao", label: "Alimentação", icon: "🍽️", color: "#3B82F6", type: "expense" },
  { id: "assinaturas", label: "Assinaturas", icon: "📱", color: "#6366F1", type: "expense" },
  { id: "beleza", label: "Beleza", icon: "💄", color: "#F472B6", type: "expense" },
  { id: "compras", label: "Compras & Presentes", icon: "🛍️", color: "#FB923C", type: "expense" },
  { id: "delivery", label: "Delivery", icon: "📦", color: "#A78BFA", type: "expense" },
  { id: "educacao", label: "Educação", icon: "📚", color: "#06B6D4", type: "expense" },
  { id: "esporte", label: "Esporte", icon: "🏋️", color: "#10B981", type: "expense" },
  { id: "impostos", label: "Impostos & Taxas", icon: "🏛️", color: "#94A3B8", type: "expense" },
  { id: "lazer", label: "Lazer", icon: "🎭", color: "#EC4899", type: "expense" },
  { id: "mercado", label: "Mercado", icon: "🛒", color: "#22C55E", type: "expense" },
  { id: "moradia", label: "Moradia", icon: "🏠", color: "#F59E0B", type: "expense" },
  { id: "pets", label: "Pets", icon: "🐾", color: "#84CC16", type: "expense" },
  { id: "saude", label: "Saúde", icon: "💊", color: "#EF4444", type: "expense" },
  { id: "transporte", label: "Transporte", icon: "🚗", color: "#8B5CF6", type: "expense" },
  { id: "vestuario", label: "Vestuário", icon: "👕", color: "#F97316", type: "expense" },
  { id: "viagem", label: "Viagem", icon: "✈️", color: "#38BDF8", type: "expense" },
  { id: "outros_desp", label: "Outros (Despesa)", icon: "❓", color: "#CBD5E1", type: "expense" },
];

const DEFAULT_INCOME_CATS = [];

// Linhas que representam pagamento da fatura (excluídas dos gastos)
const PAYMENT_KEYWORDS = [
  "pagamento fatura", "pagto fatura", "pag fatura", "pagamento cartao", "pagto cartao",
  "fatura cartao", "fatura cartão", "bill payment", "pagamento boleto cartao",
  "debito automatico fatura", "débito automático fatura",
  "pgto fatura", "pgto cartao", "pagto debito", "pagamento debito automatico",
  "pagamento recebido",
];

// Detecta parcela na descrição, retorna { parcela, total } ou null
// Exemplos: "2/12", "02/12", "PARC 2/12", "2 DE 12"
function detectInstallment(desc) {
  if (!desc) return null;
  const d = desc.toUpperCase();
  // "2/12" or "02/12" standalone or after PARC/PARCELA
  const m1 = d.match(/(?:PARC(?:ELA)?\s*)?(\d{1,2})\s*\/\s*(\d{1,2})(?!\d)/);
  if (m1) {
    const current = parseInt(m1[1]);
    const total = parseInt(m1[2]);
    if (total > 1 && current <= total) return { current, total };
  }
  // "2 DE 12"
  const m2 = d.match(/(\d{1,2})\s+DE\s+(\d{1,2})/);
  if (m2) {
    const current = parseInt(m2[1]);
    const total = parseInt(m2[2]);
    if (total > 1 && current <= total) return { current, total };
  }
  return null;
}

// ── Categorization engine ──────────────────────────────────────────────────
// Priority: 1st prefix rules → 2nd exact/contains → 3rd regex → 4th platform default

// Normalize: lowercase + remove accents
function norm(s) { return (s||"").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g,"").trim(); }

// PREFIX rules — checked BEFORE everything else (order matters)
const PREFIX_RULES = [
  // Delivery prefixes — always delivery except IFD*IFOOD CLUB
  { test: d => d.startsWith("ifd*") && !d.includes("ifood club"), cat: "delivery" },
  { test: d => d.startsWith("99food"), cat: "delivery" },
  // Transport prefixes
  { test: d => /^uber\s*uber\s*\*trip|^uber\s*\*\s*trip|^uber\s*\*ride|^dl\s*\*uberride|^uber\*trip/.test(d), cat: "transporte" },
  { test: d => /^99app\s*\*99app|^99\s{3,}/.test(d), cat: "transporte" },
  // Subscription prefixes
  { test: d => d.startsWith("ec *") || d.startsWith("ec*"), cat: "assinaturas" },
  { test: d => d.startsWith("dm*") || d.startsWith("dm *"), cat: "assinaturas" },
  { test: d => d.startsWith("uber *one") || d.startsWith("uber*one"), cat: "assinaturas" },
  { test: d => d.startsWith("tim*"), cat: "assinaturas" },
  // Health prefix
  { test: d => d.startsWith("zp *") || d.startsWith("zp*"), cat: "saude" },
  { test: d => d.startsWith("evoluservice+"), cat: "saude" },
  // Lazer prefix
  { test: d => d.startsWith("sympla*"), cat: "lazer" },
  // Shopping platform defaults (checked LAST among prefixes)
  { test: d => d.startsWith("mercadolivre*") || d.startsWith("mercado*mercadolivre"), cat: "compras" },
  { test: d => d.startsWith("amazonmktplc*"), cat: "compras" },
  { test: d => d.startsWith("pg *") || d.startsWith("pg*"), cat: "compras" },
];

// CONTAINS rules — exact substring match (after prefix check, before regex)
const CONTAINS_RULES = [
  // Delivery
  ["ze delivery","delivery"],     ["ze delivery","delivery"],
  ["zé delivery","delivery"],     ["rappi","delivery"],
  ["darkstore","delivery"],       ["bgpayfoodsltda","delivery"],
  ["james delivery","delivery"],  ["depositodelivery","delivery"],
  ["drink carioca delive","delivery"],
  // Alimentação presencial
  ["rey da alameda","alimentacao"],["marola flutuante","alimentacao"],
  ["cafe e bar","alimentacao"],   ["restaurante","alimentacao"],
  ["arcos dourados","alimentacao"],["macdonalds","alimentacao"],
  ["kfc","alimentacao"],          ["subway","alimentacao"],
  ["starbucks","alimentacao"],    ["outback","alimentacao"],
  ["habibs","alimentacao"],       ["giraffas","alimentacao"],
  ["spoleto","alimentacao"],      ["bobs ","alimentacao"],
  ["bob's","alimentacao"],        ["mamma jamma","alimentacao"],
  ["burger king","alimentacao"],  ["bk lojas","alimentacao"],
  ["temakeria","alimentacao"],    ["cheiro de mar","alimentacao"],
  ["la ola","alimentacao"],       ["bakehouse","alimentacao"],
  ["art do cafe","alimentacao"],  ["buteco","alimentacao"],
  ["bar do","alimentacao"],       ["bar imperatriz","alimentacao"],
  ["bjbs cafe","alimentacao"],    ["marilice lanches","alimentacao"],
  ["showrrasquinh","alimentacao"],["delicatessen","alimentacao"],
  ["bombar","alimentacao"],       ["chiquinhodo","alimentacao"],
  ["centenario","alimentacao"],   ["praia do flaadao","alimentacao"],
  ["jp2 comercio de alimen","alimentacao"],
  // Assinaturas
  ["apple.com/bill","assinaturas"],["applecombill","assinaturas"],
  ["amazon kindle unltd","assinaturas"],["amazon kindle","assinaturas"],
  ["ifd*ifood club","assinaturas"],["ifood club","assinaturas"],
  ["uber *one membership","assinaturas"],["google cloud","assinaturas"],
  ["dm*godaddycoml","assinaturas"],["flutterflow","assinaturas"],
  ["amazon prime","assinaturas"], ["microsoft 365","assinaturas"],
  ["adobe","assinaturas"],        ["steamgames.com","assinaturas"],
  ["clarovideo","assinaturas"],   ["claro recarga","assinaturas"],
  ["vivo recarga","assinaturas"], ["oi recarga","assinaturas"],
  ["jim.com*","assinaturas"],     ["ec *melimais","assinaturas"],
  ["clube livelo","assinaturas"], ["hbomaxassin","assinaturas"],
  // Beleza
  ["typers barber","beleza"],     ["typersbarbere","beleza"],
  ["zero grau - fonseca","beleza"],
  // Compras & Presentes
  ["mp *brabasimports","compras"],["pg *dimona","compras"],
  ["mercadolivre*artgeeklo","compras"],["dex ","compras"],
  ["utilicasa","compras"],        ["lojas americanas","compras"],
  ["hb express comercio","compras"],["jorge penteado","compras"],
  ["ifd*bramex comercio","compras"],
  // Educação
  ["escola de cozinha","educacao"],["taglivros","educacao"],
  ["auto escola","educacao"],     ["banca da uff","educacao"],
  ["mp *prmbaexame","educacao"],
  // Esporte
  ["smartfit","esporte"],         ["smart fit","esporte"],
  ["bodytech","esporte"],         ["bio ritmo","esporte"],
  // Impostos
  ["iof internacional","impostos"],["iof adicional","impostos"],
  ["iof diario","impostos"],      ["iof de atraso","impostos"],
  ["juros de divida","impostos"], ["multa de atraso","impostos"],
  ["saldo em atraso","impostos"], ["deb aut parcial","impostos"],
  ["pgto boleto parcel","impostos"],["pgto debito automatico","impostos"],
  // Lazer (presencial — sem streaming)
  ["storm entretenimento","lazer"],["ingresse ingressos","lazer"],
  ["sympla","lazer"],             ["superticket","lazer"],
  ["samba da volta","lazer"],     ["diverticidade","lazer"],
  ["00compa","lazer"],            ["ingresso","lazer"],
  // Mercado
  ["prezunic","mercado"],         ["morango das palmas","mercado"],
  ["summer fonseca","mercado"],   ["mgbebidas","mercado"],
  ["fabio drinks","mercado"],     ["beirute","mercado"],
  ["hortifruti","mercado"],       ["zona sul","mercado"],
  ["guanabara","mercado"],        ["mundial","mercado"],
  // Moradia
  ["porto seguro re","moradia"],  ["consultorio","moradia"],
  ["condominio","moradia"],       ["condomínio","moradia"],
  // Saúde
  ["drogaria tamoio","saude"],    ["drogas pacheco","saude"],
  ["drogatur","saude"],           ["drogariamoderna","saude"],
  ["drogaria cristal","saude"],   ["new otica","saude"],
  ["invisalign","saude"],         ["clinica martins","saude"],
  ["carolinapinheiro","saude"],   ["evoluservice","saude"],
  ["drogasil","saude"],           ["droga raia","saude"],
  ["farmacia","saude"],           ["farmácia","saude"],
  ["drogaria","saude"],           ["hospital","saude"],
  ["unimed","saude"],             ["amil ","saude"],
  ["sulamerica saude","saude"],   ["hapvida","saude"],
  // Transporte
  ["biataxi","transporte"],       ["estacaocostaverde","transporte"],
  ["sem parar","transporte"],     ["veloe","transporte"],
  ["conectcar","transporte"],     ["move mais","transporte"],
  ["estapar","transporte"],       ["zona azul","transporte"],
  ["riocard","transporte"],       ["bilhete unico","transporte"],
  ["metrô","transporte"],         ["metro ","transporte"],
  ["tembici","transporte"],       ["bike itau","transporte"],
  // Vestuário
  ["decathlon","vestuario"],      ["centauro","vestuario"],
  ["riachuelo","vestuario"],      ["magazine torra torra","vestuario"],
  ["world tennis","vestuario"],   ["fox tennis","vestuario"],
  ["ec *linus","vestuario"],      ["mp *intheboxcom","vestuario"],
  ["renner","vestuario"],         ["c&a","vestuario"],
  ["zara","vestuario"],           ["hering","vestuario"],
  ["marisa","vestuario"],
  // Viagem
  ["latam air","viagem"],         ["agencia morango","viagem"],
  ["vitoria rodrigues uch","viagem"],["cp parc duo gourmet","viagem"],
  ["airbnb","viagem"],            ["booking","viagem"],
  ["decolar","viagem"],           ["cvc ","viagem"],
  ["hurb","viagem"],              ["localiza","viagem"],
  ["movida","viagem"],            ["hostel","viagem"],
  // Receitas
  ["salario","salario"],          ["salário","salario"],
  ["folha","salario"],            ["holerite","salario"],
  ["freelance","freelance"],      ["freela","freelance"],
  ["rendimento","investimentos_ret"],["dividendo","investimentos_ret"],
  ["proventos","investimentos_ret"],
  ["aluguel recebido","aluguel_rec"],["inquilino","aluguel_rec"],
  ["venda","vendas"],             ["enjoei","vendas"],
  ["brecho","vendas"],
];

// REGEX rules — patterns
const REGEX_RULES = [
  // Delivery
  [/delivery|dlivery/, "delivery"],
  [/daki|cornershop|gorillas/, "delivery"],
  // Alimentação
  [/pizza|burguer|burger|grill|sushi|ramen|acai|poke/i, "alimentacao"],
  [/cantina|bistr[oô]|tratoria|trattoria/i, "alimentacao"],
  [/choperia|cervejaria|brewpub/i, "alimentacao"],
  [/padaria|panificadora|confeitaria|doceria/i, "alimentacao"],
  [/restaurante|lanchonete/i, "alimentacao"],
  // Assinaturas
  [/spotify|deezer|tidal|youtube.?premium/i, "assinaturas"],
  [/netflix|disney|primevideo|paramount|globoplay|star\+/i, "assinaturas"],
  [/crunchyroll|funimation/i, "assinaturas"],
  [/notion|canva|figma|github|heroku|vercel|netlify/i, "assinaturas"],
  [/openai|chatgpt|anthropic|midjourney/i, "assinaturas"],
  [/hbo|max.?assin|hbomaxassin/i, "assinaturas"],
  [/clarovideo|claro.*recarga|vivo.*recarga|oi.*recarga/i, "assinaturas"],
  [/icloud|google.?one|dropbox/i, "assinaturas"],
  [/apple.?com.?bill|applecombill/i, "assinaturas"],
  // Beleza
  [/barber|barbearia|barbershop/i, "beleza"],
  [/sal[aã]o|beauty|estetica|estética/i, "beleza"],
  [/nail|manicure|pedicure|cabeleireiro/i, "beleza"],
  [/sephora|boticario|boticário|o.?boticario|natura/i, "beleza"],
  [/avon|eudora|mac.?cosmetics/i, "beleza"],
  [/depila[cç][aã]o|laser.?depil|esmalte/i, "beleza"],
  // Compras
  [/presente|gift|flores|floricultura/i, "compras"],
  [/joalheria|joia|pandora|vivara/i, "compras"],
  [/kabum|pichau|terabyte/i, "compras"],
  [/amazon(?!.*kindle)|aliexpress|shopee/i, "compras"],
  [/magalu|magazine.?luiza|casas.?bahia/i, "compras"],
  [/submarino|pontofrio/i, "compras"],
  [/mercadolivre|mercadolivr/i, "compras"],
  // Educação
  [/udemy|coursera|alura|hotmart|eduzz|kiwify/i, "educacao"],
  [/livraria|livros|saraiva/i, "educacao"],
  [/faculdade|universidade|estacio|descomplica/i, "educacao"],
  [/cultura.?inglesa|wizard|fisk|ccaa|wise.?up/i, "educacao"],
  [/duolingo|babbel/i, "educacao"],
  // Esporte
  [/academia|gym|crossfit/i, "esporte"],
  [/luta|judo|jiu.?jitsu|muay.?thai/i, "esporte"],
  [/natacao|nataç[aã]o|piscina|\bclube\b/i, "esporte"],
  [/\bquadra\b|beach.?tennis|futevolei|play.?tennis/i, "esporte"],
  [/esportes|sport.?center/i, "esporte"],
  // Impostos
  [/iof|multa|juros|encargo|mora/i, "impostos"],
  // Lazer (presencial — sem streaming)
  [/ingresso|ticket|eventim|ticketmaster/i, "lazer"],
  [/cinema|cinemark|uci\b|kinoplex|cinepolis/i, "lazer"],
  [/parque|park\b|museu|teatro|show\b|concert/i, "lazer"],
  [/escape.?room|bowling|boliche|paintball|\bkart\b/i, "lazer"],
  [/karaoke|sinuca|bilhar|fliperama/i, "lazer"],
  [/\bpraia\b|beach.?club|quiosque/i, "lazer"],
  // Mercado
  [/supermercado|supermarket|hiper\b|atacad/i, "mercado"],
  [/carrefour|assai\b|extra\b|mundial\b/i, "mercado"],
  [/sacolao|verdur|quitanda/i, "mercado"],
  [/conveniencia|minuto.?pao/i, "mercado"],
  [/emporio|mercearia|armazem/i, "mercado"],
  // Moradia
  [/condomin[io]/i, "moradia"],
  [/aluguel|locacao|imobiliaria|quintoandar|quinto.?andar/i, "moradia"],
  [/light\b|enel\b|eletropaulo|celpe|cemig|cpfl/i, "moradia"],
  [/aguas.?niteroi|cedae|sabesp|sanepar/i, "moradia"],
  [/naturgy|gas.?natural|comgas|\bceg\b/i, "moradia"],
  [/seguro.?resid|tokio.?marine|bradesco.?seguro/i, "moradia"],
  [/leroy.?merlin|telhanorte|obramax/i, "moradia"],
  // Pets
  [/pet.?shop|petshop|cobasi|petz/i, "pets"],
  [/veterin[aá]ri|clinica.?animal/i, "pets"],
  [/racao|raç[aã]o|dog.?food|cat.?food/i, "pets"],
  [/banho.?tosa|grooming/i, "pets"],
  // Saúde
  [/drogaria|droga\b|farmacia|farmácia/i, "saude"],
  [/otica|optica|lentes/i, "saude"],
  [/clinica|hospital|laboratorio|\blab\b/i, "saude"],
  [/dentista|odonto|oral\b|ortodont/i, "saude"],
  [/fisio|psic[oó]|nutri\b|fono/i, "saude"],
  [/sulamerica.?saude|amil\b|unimed|hapvida/i, "saude"],
  // Transporte
  [/uber.?trip|uber.?ride|uberrides/i, "transporte"],
  [/99pop|99app|99taxi|^99\s/i, "transporte"],
  [/estacionamento|estapar|zona.?azul/i, "transporte"],
  [/combustivel|posto\b|shell\b|ipiranga|br.?distribui/i, "transporte"],
  [/pedagio|semparar|veloe|conectcar|move.?mais/i, "transporte"],
  [/metr[oô]|vlt\b|barcas|rodoviaria|supervia/i, "transporte"],
  [/indriver|cabify|lyft/i, "transporte"],
  [/tembici|bike.?itau|grin\b|lime\b/i, "transporte"],
  // Vestuário
  [/renner|riachuelo|hering|marisa\b/i, "vestuario"],
  [/shein|netshoes|dafiti/i, "vestuario"],
  [/\bnikeb|adidas|puma\b|asics/i, "vestuario"],
  [/calcado|sapato|havaianas|arezzo/i, "vestuario"],
  // Viagem
  [/gol\b.*linhas|azul.*linhas|latam|tap\b|avianca/i, "viagem"],
  [/booking\b|hotels\b|trivago|hostel/i, "viagem"],
  [/decolar|hurb|maxmilhas|123milhas/i, "viagem"],
  [/aluguel.?carro|localiza|movida|unidas/i, "viagem"],
  [/seguro.?viag|travel.?insur|assist.?card/i, "viagem"],
  [/duty.?free|tax.?free/i, "viagem"],
];

function isPaymentTransaction(desc) {
  const d = norm(desc);
  return PAYMENT_KEYWORDS.some(kw => d.includes(norm(kw)));
}

function categorize(desc) {
  if (!desc) return null;
  const d = norm(desc);

  // 1. Prefix rules
  for (const rule of PREFIX_RULES) {
    if (rule.test(d)) return rule.cat;
  }

  // 2. Contains rules (exact substring, normalized)
  for (const [kw, cat] of CONTAINS_RULES) {
    if (d.includes(norm(kw))) return cat;
  }

  // 3. Regex rules
  for (const [rx, cat] of REGEX_RULES) {
    if (rx.test(d)) return cat;
  }

  // 4. MP* platform default → compras
  if (d.startsWith("mp *") || d.startsWith("mp*")) return "compras";

  return null;
}

/* ══════════════════════════════════════════════════════════════
   HELPERS
   ══════════════════════════════════════════════════════════════ */

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result; // data:image/...;base64,...
      resolve(result.split(",")[1]);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/* ══════════════════════════════════════════════════════════════
   PARSERS
   ══════════════════════════════════════════════════════════════ */

const MONTHS = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];
const MONTHS_FULL = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];
const fmt = v => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const fmtPct = v => (v * 100).toFixed(1) + "%";

function parseCurrency(val) {
  if (typeof val === "number") return val;
  if (!val) return 0;
  let s = String(val).replace(/\s/g, "").replace("R$", "").trim();
  if (s.includes(",") && s.includes(".")) {
    s = s.lastIndexOf(",") > s.lastIndexOf(".") ? s.replace(/\./g, "").replace(",", ".") : s.replace(/,/g, "");
  } else if (s.includes(",")) s = s.replace(",", ".");
  const n = parseFloat(s);
  return isNaN(n) ? 0 : n;
}

function parseDate(val) {
  if (!val) return null;
  const s = String(val).trim();
  if (/^\d{8}$/.test(s)) return new Date(+s.slice(0,4), +s.slice(4,6)-1, +s.slice(6,8));
  if (/^\d{14}/.test(s)) return new Date(+s.slice(0,4), +s.slice(4,6)-1, +s.slice(6,8));
  const br = s.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})$/);
  if (br) { const y = br[3].length === 2 ? "20" + br[3] : br[3]; return new Date(+y, +br[2] - 1, +br[1]); }
  const iso = s.match(/^(\d{4})[\/\-](\d{1,2})[\/\-](\d{1,2})$/);
  if (iso) return new Date(+iso[1], +iso[2] - 1, +iso[3]);
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

function parseCSV(text) {
  const lines = text.split(/\r?\n/).filter(l => l.trim());
  if (lines.length < 2) return [];
  const sep = lines[0].includes(";") ? ";" : lines[0].includes("\t") ? "\t" : ",";
  const headers = lines[0].split(sep).map(h => h.trim().replace(/^"|"$/g, "").toLowerCase());
  return lines.slice(1).map(line => {
    const vals = line.split(sep).map(v => v.trim().replace(/^"|"$/g, ""));
    const row = {};
    headers.forEach((h, i) => { row[h] = vals[i] || ""; });
    return row;
  }).filter(r => Object.values(r).some(v => v));
}

function parseOFX(text) {
  const txns = [];
  const stmtTrns = text.split(/<STMTTRN>/gi).slice(1);
  for (const block of stmtTrns) {
    const get = (tag) => { const m = block.match(new RegExp(`<${tag}>([^<\\n]+)`, "i")); return m ? m[1].trim() : ""; };
    const amt = parseCurrency(get("TRNAMT"));
    const date = parseDate(get("DTPOSTED"));
    const desc = get("MEMO") || get("NAME") || get("FITID") || "Sem descrição";
    if (desc) txns.push({ date, description: desc, value: Math.abs(amt), originalAmount: amt, isCredit: amt > 0 });
  }
  return txns;
}

async function extractPDFText(buf) {
  try {
    const pdfjsLib = await import("pdfjs-dist/legacy/build/pdf");
    pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;
    const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
    const pageTexts = [];
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const content = await page.getTextContent();
      // Group items by approximate Y position to preserve row structure
      const rows = {};
      for (const item of content.items) {
        const y = Math.round(item.transform[5]);
        if (!rows[y]) rows[y] = [];
        rows[y].push(item.str);
      }
      const sortedYs = Object.keys(rows).map(Number).sort((a, b) => b - a);
      for (const y of sortedYs) {
        const line = rows[y].join(" ").trim();
        if (line) pageTexts.push(line);
      }
    }
    return pageTexts.join("\n");
  } catch (e) {
    console.error("pdfjs error", e);
    return "";
  }
}

function extractTxns(rows, fileName) {
  const keys = rows.length > 0 ? Object.keys(rows[0]) : [];
  const find = (cs) => { for (const c of cs) { const f = keys.find(k => k.includes(c)); if (f) return f; } return null; };
  const dateKey = find(["data","date","dt","posted"]) || keys[0];
  const descKey = find(["descrição","descricao","description","desc","estabelecimento","titulo","título","lançamento","lancamento","nome","histórico","historico","memo","name"]) || keys[1];
  const valKey = find(["valor","value","amount","quantia","debito","débito","total","trnamt"]) || keys[keys.length > 2 ? 2 : keys.length - 1];

  return rows.map((r, i) => {
    const rawVal = parseCurrency(r[valKey]);
    if (rawVal === 0) return null;
    const desc = r[descKey] || "Sem descrição";
    const isPayment = isPaymentTransaction(desc);
    const cat = categorize(desc);
    // In card invoices everything is an expense — never auto-assign income categories
    const catId = cat && !DEFAULT_INCOME_CATS.some(c => c.id === cat) ? cat : (cat || "outros_desp");
    const installment = detectInstallment(desc);
    return {
      id: `${fileName}-${i}-${Date.now()}`,
      date: r.date instanceof Date ? r.date : parseDate(r[dateKey]),
      description: desc,
      value: Math.abs(rawVal),
      category: catId,
      source: fileName,
      needsReview: !cat || DEFAULT_INCOME_CATS.some(c => c.id === cat),
      type: "expense",
      isPayment,
      installment,
    };
  }).filter(Boolean);
}

// Month abbreviation → number map for Brazilian PDF statements
const BR_MON = { jan:1,fev:2,mar:3,abr:4,mai:5,jun:6,jul:7,ago:8,set:9,out:10,nov:11,dez:12 };

function parseBRDate(dayStr, monStr, yearHint) {
  const d = parseInt(dayStr);
  const m = BR_MON[monStr.toLowerCase()];
  if (!m) return null;
  // If month is after current month in yearHint, it belongs to the previous year
  const y = (yearHint && m > new Date().getMonth() + 1 && yearHint > 2000)
    ? yearHint - 1 : (yearHint || new Date().getFullYear());
  return new Date(y, m - 1, d);
}

function parseNubankPDF(text, fileName) {
  const txns = [];
  const lines = text.split("\n").map(l => l.trim()).filter(Boolean);

  // Detect invoice year from header lines like "FATURA 12 JAN 2026"
  let invoiceYear = new Date().getFullYear();
  for (const l of lines) {
    const ym = l.match(/FATURA\s+\d+\s+\w+\s+(\d{4})/i);
    if (ym) { invoiceYear = parseInt(ym[1]); break; }
  }

  // Find "TRANSAÇÕES" section start — skip everything before
  let inTxns = false;
  // Stop when we hit "Pagamentos" section
  const STOP_WORDS = ["pagamentos", "pagamentos e financiamentos", "financiamentos"];

  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i];
    const lower = raw.toLowerCase();

    if (!inTxns) {
      if (/^transações/i.test(raw)) { inTxns = true; }
      continue;
    }

    // Stop at payments section
    if (STOP_WORDS.some(w => lower.startsWith(w))) break;

    // Match transaction line patterns:
    // Pattern A: "05 DEZ •••• 1224 Description R$ 99,99"
    // Pattern B: "05 DEZ Description R$ 99,99"  (NuPay lines)
    // Pattern C: "05 DEZ NU Description R$ 99,99"
    // Value is always last: "R$ XX,XX" or just the number
    const valMatch = raw.match(/R\$\s*([\d.,]+)\s*$/);
    if (!valMatch) continue;

    const valueStr = valMatch[1];
    const value = parseCurrency(valueStr);
    if (value <= 0) continue;

    // Remove the trailing value part to get the date + description
    const withoutVal = raw.replace(/R\$\s*[\d.,]+\s*$/, "").trim();

    // Extract date: starts with "DD MMM" (e.g. "05 DEZ", "01 JAN")
    const dateMatch = withoutVal.match(/^(\d{1,2})\s+(jan|fev|mar|abr|mai|jun|jul|ago|set|out|nov|dez)\b/i);
    if (!dateMatch) continue;

    const date = parseBRDate(dateMatch[1], dateMatch[2], invoiceYear);

    // Remove date from front, then strip card number fragments like "•••• 1224" or "NU"
    let desc = withoutVal.slice(dateMatch[0].length).trim();
    desc = desc.replace(/^[•\s]+\d{4}\s*/,"").replace(/^nu\s+/i,"").trim();

    if (!desc) continue;

    // Check for installment pattern in description: "- Parcela N/T" or "Parcela N/T"
    const installment = detectInstallment(desc);

    // Skip payment/credit lines that slipped through
    if (isPaymentTransaction(desc)) continue;

    const cat = categorize(desc);
    const catId = cat && !DEFAULT_INCOME_CATS.some(c => c.id === cat) ? cat : (cat || "outros_desp");

    txns.push({
      id: `${fileName}-${txns.length}-${Date.now()}`,
      date,
      description: desc,
      value,
      category: catId,
      source: fileName,
      needsReview: !cat || DEFAULT_INCOME_CATS.some(c => c.id === cat),
      type: "expense",
      isPayment: false,
      installment,
    });
  }

  return txns;
}

function parseInterPDF(text, fileName) {
  const txns = [];
  const lines = text.split("\n").map(l => l.trim()).filter(Boolean);

  // Detect invoice year from header like "02/02/2026" or "VENCIMENTO 02/02/2026"
  let invoiceYear = new Date().getFullYear();
  for (const l of lines) {
    const ym = l.match(/\d{2}\/\d{2}\/(\d{4})/);
    if (ym) { invoiceYear = parseInt(ym[1]); break; }
  }

  // Inter date format: "DD de MMM. AAAA" or "DD de MMM AAAA"
  // e.g. "05 de set. 2025", "01 de jan. 2026"
  function parseInterDate(str) {
    const m = str.match(/(\d{1,2})\s+de\s+(\w+)\.?\s+(\d{4})/i);
    if (!m) return null;
    return parseBRDate(m[1], m[2], parseInt(m[3]));
  }

  let inDespesas = false;

  // Stop keywords that mark end of transactions section
  const STOP_RE = /^(limite de cr[eé]dito total|pr[oó]xima fatura|fale com a gente|encargos financeiros)/i;
  // Skip card total lines and header lines
  const SKIP_RE = /^(total cart[aã]o|data\s+movimenta|benefici[aá]rio)/i;
  // Card section header: "CARTÃO XXXX****XXXX"
  const CARD_RE = /^CART[AÃ]O\s+\d{4}\*+\d{4}/i;

  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i];

    if (!inDespesas) {
      if (/^despesas da fatura/i.test(raw)) { inDespesas = true; }
      continue;
    }

    if (STOP_RE.test(raw)) break;
    if (SKIP_RE.test(raw) || CARD_RE.test(raw)) continue;

    // Each transaction line starts with a date: "DD de MMM. AAAA" or "DD de MMM AAAA"
    const dateMatch = raw.match(/^(\d{1,2}\s+de\s+\w+\.?\s+\d{4})/i);
    if (!dateMatch) continue;

    const date = parseInterDate(dateMatch[1]);
    if (!date) continue;

    // Remove date from front
    let rest = raw.slice(dateMatch[0].length).trim();

    // Value is at the end: "R$ XX,XX" or "+ R$ XX,XX"
    const valMatch = rest.match(/(\+\s*)?R\$\s*([\d.,]+)\s*$/);
    if (!valMatch) continue;

    const isCredit = valMatch[1] && valMatch[1].includes("+");
    if (isCredit) continue; // skip credits/refunds

    const value = parseCurrency(valMatch[2]);
    if (value <= 0) continue;

    // Remove value from end, then strip beneficiario separator " - "
    let desc = rest.replace(/(\+\s*)?R\$\s*[\d.,]+\s*$/, "").trim();
    // Remove trailing " - BENEFICIÁRIO" if present (beneficiário column)
    desc = desc.replace(/\s+-\s*$/, "").trim();
    // Also strip trailing " - " followed by beneficiário content that is left
    // Inter puts beneficiário between description and value separated by spaces
    // Since pdfjs merges columns, we just clean trailing " - " artifacts
    desc = desc.replace(/\s{2,}-\s*$/, "").trim();

    if (!desc || isPaymentTransaction(desc)) continue;

    // Also skip IOF lines (they are tax entries we may want to keep or skip)
    // We'll keep them as "impostos" category

    const installment = detectInstallment(desc);
    const cat = categorize(desc);
    const catId = cat && !DEFAULT_INCOME_CATS.some(c => c.id === cat) ? cat : (cat || "outros_desp");

    txns.push({
      id: `${fileName}-${txns.length}-${Date.now()}`,
      date,
      description: desc,
      value,
      category: catId,
      source: fileName,
      needsReview: !cat || DEFAULT_INCOME_CATS.some(c => c.id === cat),
      type: "expense",
      isPayment: false,
      installment,
    });
  }

  return txns;
}

async function processFile(file) {
  const ext = file.name.split(".").pop().toLowerCase();

  if (ext === "ofx" || ext === "qfx") {
    const text = await file.text();
    const txns = parseOFX(text);
    const mapped = txns.map((t, i) => {
      const cat = categorize(t.description);
      const isPayment = isPaymentTransaction(t.description);
      const catId = cat && !DEFAULT_INCOME_CATS.some(c => c.id === cat) ? cat : (cat || "outros_desp");
      const installment = detectInstallment(t.description);
      return {
        id: `${file.name}-${i}-${Date.now()}`,
        date: t.date, description: t.description, value: t.value,
        category: catId,
        source: file.name,
        needsReview: !cat || DEFAULT_INCOME_CATS.some(c => c.id === cat),
        type: "expense",
        isPayment,
        installment,
      };
    });
    return { name: file.name, count: mapped.length, transactions: mapped };
  }

  if (ext === "pdf") {
    const buf = await file.arrayBuffer();
    const text = await extractPDFText(buf);
    if (!text.trim()) return { name: file.name, count: 0, transactions: [] };

    // Detect bank by content fingerprints
    const upper = text.slice(0, 3000).toUpperCase();
    const isNubank = upper.includes("NU PAGAMENTOS") || upper.includes("NUBANK") ||
                     upper.includes("NUPAY") || /nubank/i.test(file.name);
    const isInter = upper.includes("BANCO INTER") || upper.includes("BANCOINTER") ||
                    upper.includes("DESPESAS DA FATURA") || /inter/i.test(file.name);

    if (isNubank) {
      const txns = parseNubankPDF(text, file.name);
      return { name: file.name, count: txns.length, transactions: txns };
    }

    if (isInter) {
      const txns = parseInterPDF(text, file.name);
      return { name: file.name, count: txns.length, transactions: txns };
    }

    // Generic PDF: try date+desc+value line pattern
    const pat = /(\d{1,2}[\/\-]\d{1,2}[\/\-]?\d{0,4})\s+(.+?)\s+([\-]?\d{1,3}(?:[.,]\d{3})*[.,]\d{2})\s*$/gm;
    const rows = []; let match;
    while ((match = pat.exec(text))) rows.push({ data: match[1], "descrição": match[2].trim(), valor: match[3] });
    if (rows.length === 0) { const csvRows = parseCSV(text); const txns = extractTxns(csvRows, file.name); return { name: file.name, count: txns.length, transactions: txns }; }
    const txns = extractTxns(rows, file.name);
    return { name: file.name, count: txns.length, transactions: txns };
  }

  // CSV, TXT, TSV, XLS, XLSX
  const text = await file.text();
  const rows = parseCSV(text);
  const txns = extractTxns(rows, file.name);
  return { name: file.name, count: txns.length, transactions: txns };
}

/* ══════════════════════════════════════════════════════════════
   STYLES
   ══════════════════════════════════════════════════════════════ */

// ── Brand tokens ──────────────────────────────────────────────
const BRAND = { green: "#40916c", lime: "#D0F57A", bg: "#F4F6F4", surface: "#FFFFFF", text: "#1C2B20", muted: "#6B7A6E", border: "#DDE8E0" };

const S = {
  app: { fontFamily: "'SuisseIntl', -apple-system, sans-serif", background: BRAND.bg, minHeight: "100vh", color: BRAND.text },
  sidebar: (open, mobile) => ({
    width: open ? 240 : (mobile ? 0 : 64),
    background: BRAND.green,
    borderRight: "none",
    padding: open ? "20px 0" : "20px 0",
    position: "fixed",
    top: 0,
    left: 0,
    bottom: 0,
    overflowY: "auto",
    overflowX: "hidden",
    zIndex: 100,
    transition: "width 0.22s cubic-bezier(.4,0,.2,1)",
    boxShadow: mobile && open ? "4px 0 24px rgba(0,0,0,0.18)" : "2px 0 12px rgba(64,145,108,0.10)",
  }),
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 99, backdropFilter: "blur(2px)" },
  main: (sidebarOpen, mobile) => ({
    marginLeft: mobile ? 0 : (sidebarOpen ? 240 : 64),
    padding: mobile ? "72px 16px 24px" : "24px 32px",
    minHeight: "100vh",
    transition: "margin-left 0.22s cubic-bezier(.4,0,.2,1)",
  }),
  topbar: (mobile) => ({
    display: mobile ? "flex" : "none",
    alignItems: "center",
    gap: 12,
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    height: 56,
    background: BRAND.green,
    borderBottom: "none",
    padding: "0 16px",
    zIndex: 98,
  }),
  logo: { fontSize: 15, fontWeight: 800, color: BRAND.lime, letterSpacing: "-0.01em", paddingLeft: 8, whiteSpace: "nowrap", overflow: "hidden" },
  accent: { color: BRAND.lime },
  navItem: (active) => ({
    display: "flex", alignItems: "center", gap: 10, padding: "11px 20px",
    fontSize: 15, fontWeight: active ? 600 : 500,
    color: active ? BRAND.lime : "rgba(255,255,255,0.75)",
    background: active ? "rgba(208,245,122,0.12)" : "transparent",
    cursor: "pointer",
    borderLeft: active ? `3px solid ${BRAND.lime}` : "3px solid transparent",
    transition: "all 0.15s",
    whiteSpace: "nowrap", overflow: "hidden",
  }),
  card: { background: BRAND.surface, borderRadius: 16, padding: 20, border: `1px solid ${BRAND.border}`, boxShadow: "0 1px 4px rgba(64,145,108,0.06)", marginBottom: 16 },
  cardT: { fontSize: 14, fontWeight: 700, color: BRAND.text, marginBottom: 14, display: "flex", alignItems: "center", gap: 8 },
  metricRow: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginBottom: 16 },
  metric: { background: BRAND.surface, borderRadius: 14, padding: "16px 14px", border: `1px solid ${BRAND.border}`, boxShadow: "0 1px 4px rgba(64,145,108,0.06)", display: "flex", justifyContent: "space-between", alignItems: "center" },
  mVal: { fontSize: 20, fontWeight: 800, color: BRAND.text, letterSpacing: "-0.02em" },
  mLbl: { fontSize: 11, fontWeight: 500, color: BRAND.muted, marginBottom: 2 },
  mIcon: { width: 38, height: 38, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  btn1: { display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 16px", borderRadius: 10, border: "none", background: BRAND.green, color: "#fff", fontWeight: 600, fontSize: 13, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" },
  btn2: { display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 16px", borderRadius: 10, border: `1px solid ${BRAND.border}`, background: BRAND.surface, color: BRAND.text, fontWeight: 600, fontSize: 13, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" },
  btnSm: { display: "inline-flex", alignItems: "center", gap: 4, padding: "5px 10px", borderRadius: 7, border: "none", fontWeight: 600, fontSize: 12, cursor: "pointer", fontFamily: "inherit" },
  btnDanger: { display: "inline-flex", alignItems: "center", gap: 4, padding: "5px 10px", borderRadius: 7, border: "none", background: "#FEE2E2", color: "#DC2626", fontWeight: 600, fontSize: 12, cursor: "pointer", fontFamily: "inherit" },
  dz: (on) => ({ border: `2px dashed ${on ? BRAND.green : BRAND.border}`, borderRadius: 14, padding: "36px 16px", textAlign: "center", cursor: "pointer", background: on ? "#EAF5EF" : BRAND.bg, transition: "all 0.15s" }),
  badge: (c) => ({ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 9px", borderRadius: 20, fontSize: 11, fontWeight: 600, background: c + "14", color: c, border: `1px solid ${c}30` }),
  th: { padding: "9px 10px", fontSize: 11, fontWeight: 600, color: BRAND.muted, textAlign: "left", textTransform: "uppercase", letterSpacing: "0.04em", borderBottom: `1px solid ${BRAND.border}`, background: BRAND.bg, whiteSpace: "nowrap" },
  td: { padding: "9px 10px", fontSize: 13, borderBottom: `1px solid ${BRAND.border}`, color: BRAND.text },
  sel: { background: BRAND.bg, border: `1px solid ${BRAND.border}`, color: BRAND.text, borderRadius: 8, padding: "6px 10px", fontSize: 13, cursor: "pointer", fontFamily: "inherit", outline: "none" },
  input: { background: BRAND.bg, border: `1px solid ${BRAND.border}`, color: BRAND.text, borderRadius: 8, padding: "8px 12px", fontSize: 13, fontFamily: "inherit", outline: "none", width: "100%" },
  chip: { display: "inline-flex", alignItems: "center", gap: 4, padding: "4px 10px", borderRadius: 20, border: `1px solid ${BRAND.border}`, fontSize: 11, fontWeight: 500, color: BRAND.text, background: BRAND.surface },
  pageTitle: { fontSize: 28, fontWeight: 400, color: BRAND.text, letterSpacing: "-0.03em", marginBottom: 2, fontFamily: "'LyonDisplay', Georgia, serif" },
  pageDesc: { fontSize: 13, color: BRAND.muted, marginBottom: 20 },
  tag: (color) => ({ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 7px", borderRadius: 6, fontSize: 11, fontWeight: 600, background: color + "18", color }),
  modal: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, backdropFilter: "blur(4px)" },
  modalContent: { background: BRAND.surface, borderRadius: 20, padding: 24, maxWidth: 500, width: "92%", maxHeight: "88vh", overflowY: "auto", boxShadow: "0 24px 48px rgba(0,0,0,0.12)" },
};

/* ══════════════════════════════════════════════════════════════
   SHARED COMPONENTS
   ══════════════════════════════════════════════════════════════ */

const TT = ({ active, payload, label }) => {
  if (!active || !payload?.[0]) return null;
  return (<div style={{ background:"#fff",border:"1px solid #E5E7EB",borderRadius:10,padding:"8px 12px",boxShadow:"0 4px 12px rgba(0,0,0,0.08)",fontSize:12,fontFamily:"'SuisseIntl',sans-serif" }}>
    <div style={{ fontWeight:600,color:"#111" }}>{label}</div>
    {payload.map((p,i) => <div key={i} style={{ color:p.color||"#3B82F6",marginTop:2 }}>{p.name}: {fmt(p.value)}</div>)}
  </div>);
};

const DTT = ({ active, payload }) => {
  if (!active || !payload?.[0]) return null;
  const d = payload[0].payload;
  return (<div style={{ background:"#fff",border:"1px solid #E5E7EB",borderRadius:10,padding:"8px 12px",boxShadow:"0 4px 12px rgba(0,0,0,0.08)",fontSize:12,fontFamily:"'SuisseIntl',sans-serif" }}>
    <div style={{ fontWeight:700,color:"#111" }}>{d.icon||""} {d.name}</div>
    <div style={{ color:"#6B7280",marginTop:2 }}>{fmt(d.value)} · {fmtPct(d.pct||0)}</div>
  </div>);
};

function CatSelect({ value, onChange, categories }) {
  const cat = categories.find(c => c.id === value);
  return (
    <select value={value} onChange={e => onChange(e.target.value)} style={{ ...S.sel, borderColor: (cat?.color || "#ccc") + "60", background: (cat?.color || "#ccc") + "08", fontSize: 12 }}>
      {categories.map(c => <option key={c.id} value={c.id}>{c.icon} {c.label}</option>)}
    </select>
  );
}

function Modal({ open, onClose, title, children, wide, noClickOutside }) {
  if (!open) return null;
  return (
    <div style={S.modal} onClick={noClickOutside ? undefined : onClose}>
      <div style={{ ...S.modalContent, maxWidth: wide ? 700 : 500 }} onClick={e => e.stopPropagation()}>
        <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18 }}>
          <div style={{ fontSize:16,fontWeight:700 }}>{title}</div>
          <button onClick={onClose} style={{ ...S.btnSm, background:"#F3F4F6",color:"#6B7280" }}><X size={14}/></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function InputRow({ label, value, onChange, type = "text", placeholder = "", prefix = "" }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: "#6B7280", display: "block", marginBottom: 4 }}>{label}</label>
      <div style={{ position: "relative" }}>
        {prefix && <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", fontSize: 13, color: "#9CA3AF" }}>{prefix}</span>}
        <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
          style={{ ...S.input, paddingLeft: prefix ? 32 : 12 }} />
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   MODAL COMPONENTS
   ══════════════════════════════════════════════════════════════ */

function NewCatModal({ onClose, onSave }) {
  const [name, setName] = useState("");
  const [icon, setIcon] = useState("📌");
  const [color, setColor] = useState("#3B82F6");
  const [type, setType] = useState("expense");
  return (
    <Modal open={true} onClose={onClose} title="Nova Categoria" noClickOutside>
      <InputRow label="Nome" value={name} onChange={setName} placeholder="Ex: Farmácia"/>
      <InputRow label="Emoji / Ícone" value={icon} onChange={setIcon} placeholder="🏷️"/>
      <div style={{ marginBottom:12 }}>
        <label style={{ fontSize:12,fontWeight:600,color:"#6B7280",display:"block",marginBottom:4 }}>Cor</label>
        <div style={{ display:"flex",alignItems:"center",gap:10 }}>
          <input type="color" value={color} onChange={e => setColor(e.target.value)} style={{ width:40,height:36,border:"1px solid #E5E7EB",borderRadius:8,cursor:"pointer",padding:2 }}/>
          <input value={color} onChange={e => setColor(e.target.value)} style={{ ...S.input, flex:1 }} placeholder="#3B82F6"/>
        </div>
      </div>
      <div style={{ marginBottom:14 }}>
        <label style={{ fontSize:12,fontWeight:600,color:"#6B7280",display:"block",marginBottom:4 }}>Tipo</label>
        <div style={{ display:"flex",gap:8 }}>
          {[["expense","Despesa"],["income","Receita"]].map(([v,l]) => (
            <button key={v} onClick={() => setType(v)} style={{ ...S.btn1,background:type===v?BRAND.green:"#F3F4F6",color:type===v?"#fff":"#6B7280",flex:1 }}>{l}</button>
          ))}
        </div>
      </div>
      <button style={{ ...S.btn1,width:"100%",justifyContent:"center" }} onClick={() => {
        if (name.trim()) {
          const id = name.toLowerCase().replace(/\s+/g,"_").replace(/[^a-z0-9_]/g,"") + "_" + Date.now();
          onSave({ id, label: name.trim(), icon, color, type });
        }
      }}><Check size={14}/> Criar Categoria</button>
    </Modal>
  );
}

function EditCatModal({ cat, onClose, onSave }) {
  const [name, setName] = useState(cat.label);
  const [icon, setIcon] = useState(cat.icon);
  const [color, setColor] = useState(cat.color);
  return (
    <Modal open={true} onClose={onClose} title="Editar Categoria" noClickOutside>
      <InputRow label="Nome" value={name} onChange={setName}/>
      <InputRow label="Emoji / Ícone" value={icon} onChange={setIcon}/>
      <div style={{ marginBottom:14 }}>
        <label style={{ fontSize:12,fontWeight:600,color:"#6B7280",display:"block",marginBottom:4 }}>Cor</label>
        <div style={{ display:"flex",alignItems:"center",gap:10 }}>
          <input type="color" value={color} onChange={e => setColor(e.target.value)} style={{ width:40,height:36,border:"1px solid #E5E7EB",borderRadius:8,cursor:"pointer",padding:2 }}/>
          <input value={color} onChange={e => setColor(e.target.value)} style={{ ...S.input, flex:1 }}/>
        </div>
      </div>
      <button style={{ ...S.btn1,width:"100%",justifyContent:"center" }} onClick={() => onSave({ ...cat, label: name, icon, color })}><Check size={14}/> Salvar</button>
    </Modal>
  );
}

function EditAccountModal({ item, onClose, onSave }) {
  const [name, setName] = useState(item.name);
  const [bal, setBal] = useState(String(item.balance));
  const [bank, setBank] = useState(item.bank || "");
  const [type, setType] = useState(item.type || "checking");
  const [icon, setIcon] = useState(item.icon || "🏦");
  const [color, setColor] = useState(item.color || "#6B7280");
  const BANKS = ["Nubank","Itaú","Bradesco","Santander","Banco do Brasil","Caixa","Inter","C6 Bank","XP","BTG","Sicoob","Outro"];
  return (
    <Modal open={true} onClose={onClose} title="Editar Conta" noClickOutside>
      <InputRow label="Nome / Apelido" value={name} onChange={setName}/>
      <div style={{ marginBottom:12 }}>
        <label style={{ fontSize:12,fontWeight:600,color:"#6B7280",display:"block",marginBottom:4 }}>Banco</label>
        <select value={bank} onChange={e => setBank(e.target.value)} style={{ ...S.sel, width:"100%" }}>
          <option value="">Selecione...</option>
          {BANKS.map(b => <option key={b} value={b}>{b}</option>)}
        </select>
      </div>
      <div style={{ marginBottom:12 }}>
        <label style={{ fontSize:12,fontWeight:600,color:"#6B7280",display:"block",marginBottom:4 }}>Tipo</label>
        <select value={type} onChange={e => setType(e.target.value)} style={{ ...S.sel, width:"100%" }}>
          <option value="checking">Conta Corrente</option>
          <option value="savings">Poupança</option>
          <option value="investment">Investimento</option>
        </select>
      </div>
      <InputRow label="Saldo (R$)" value={bal} onChange={setBal} type="number"/>
      <div style={{ display:"flex",gap:10,marginBottom:14 }}>
        <div style={{ flex:1 }}>
          <label style={{ fontSize:12,fontWeight:600,color:"#6B7280",display:"block",marginBottom:4 }}>Emoji</label>
          <input value={icon} onChange={e => setIcon(e.target.value)} style={{ ...S.input }}/>
        </div>
        <div style={{ flex:1 }}>
          <label style={{ fontSize:12,fontWeight:600,color:"#6B7280",display:"block",marginBottom:4 }}>Cor</label>
          <div style={{ display:"flex",gap:6 }}>
            <input type="color" value={color} onChange={e => setColor(e.target.value)} style={{ width:36,height:36,border:"1px solid #E5E7EB",borderRadius:8,cursor:"pointer",padding:2 }}/>
            <input value={color} onChange={e => setColor(e.target.value)} style={{ ...S.input, flex:1 }}/>
          </div>
        </div>
      </div>
      <button style={{ ...S.btn1,width:"100%",justifyContent:"center" }} onClick={() => onSave({ name, bank, type, balance: parseFloat(bal)||0, icon, color })}><Check size={14}/> Salvar</button>
    </Modal>
  );
}

function EditCardModal({ item, onClose, onSave }) {
  const [name, setName] = useState(item.name);
  const [closing, setClosing] = useState(String(item.closing||5));
  const [due, setDue] = useState(String(item.due||15));
  const [bank, setBank] = useState(item.bank || "");
  const [color, setColor] = useState(item.color || "#6B7280");
  const BANKS = ["Nubank","Itaú","Bradesco","Santander","Banco do Brasil","Caixa","Inter","C6 Bank","XP","BTG","Outro"];
  return (
    <Modal open={true} onClose={onClose} title="Editar Cartão" noClickOutside>
      <InputRow label="Nome do Cartão" value={name} onChange={setName}/>
      <div style={{ marginBottom:12 }}>
        <label style={{ fontSize:12,fontWeight:600,color:"#6B7280",display:"block",marginBottom:4 }}>Banco / Emissor</label>
        <select value={bank} onChange={e => setBank(e.target.value)} style={{ ...S.sel, width:"100%" }}>
          <option value="">Selecione...</option>
          {BANKS.map(b => <option key={b} value={b}>{b}</option>)}
        </select>
      </div>
      <div style={{ display:"flex",gap:10,marginBottom:12 }}>
        <InputRow label="Fechamento (dia)" value={closing} onChange={setClosing} type="number"/>
        <InputRow label="Vencimento (dia)" value={due} onChange={setDue} type="number"/>
      </div>
      <div style={{ marginBottom:14 }}>
        <label style={{ fontSize:12,fontWeight:600,color:"#6B7280",display:"block",marginBottom:4 }}>Cor</label>
        <div style={{ display:"flex",gap:6 }}>
          <input type="color" value={color} onChange={e => setColor(e.target.value)} style={{ width:36,height:36,border:"1px solid #E5E7EB",borderRadius:8,cursor:"pointer",padding:2 }}/>
          <input value={color} onChange={e => setColor(e.target.value)} style={{ ...S.input, flex:1 }}/>
        </div>
      </div>
      <button style={{ ...S.btn1,width:"100%",justifyContent:"center" }} onClick={() => onSave({ name, bank, closing: parseInt(closing)||5, due: parseInt(due)||15, color })}><Check size={14}/> Salvar</button>
    </Modal>
  );
}

/* ══════════════════════════════════════════════════════════════
   MAIN APP
   ══════════════════════════════════════════════════════════════ */

/* ── DB helpers ─────────────────────────────────────────────── */
function txToDb(t, uid) {
  return {
    id: t.id, user_id: uid,
    date: t.date ? (t.date instanceof Date ? t.date.toISOString().slice(0,10) : t.date) : null,
    description: t.description, value: t.value, type: t.type,
    category: t.category, source: t.source,
    import_id: t.importId, import_name: t.importName,
    needs_review: t.needsReview, installment: t.installment || null,
  };
}
function dbToTx(row) {
  return {
    id: row.id,
    date: row.date ? new Date(row.date + "T00:00:00") : null,
    description: row.description, value: Number(row.value), type: row.type,
    category: row.category, source: row.source,
    importId: row.import_id, importName: row.import_name,
    needsReview: row.needs_review, installment: row.installment,
  };
}
function dbToInv(row) {
  return { ...row, month: row.month, year: row.year, bankId: row.bank_id, imageUrl: row.image_url, manualItems: row.manual_items || [], extracting: false, extractError: null };
}
function invToDb(inv, uid) {
  return { id: inv.id, user_id: uid, name: inv.name, month: inv.month, year: inv.year, bank_id: inv.bankId, image_url: inv.imageUrl, manual_items: inv.manualItems || [], total: inv.total || 0 };
}

export default function App() {
  const [authUser, setAuthUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [page, setPage] = useState("dashboard");
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  // Review flow: pending import awaiting user confirmation
  // reviewDraft = { type: "structured"|"image", items: [...], file, imageUrl, name }
  const [reviewDraft, setReviewDraft] = useState(null);

  // Core data
  const [categories, setCategories] = useState([...DEFAULT_EXPENSE_CATS, ...DEFAULT_INCOME_CATS]);
  const [transactions, setTransactions] = useState([]);
  // User-learned categorization rules: { keyword -> catId }
  const [userRules, setUserRules] = useState({});
  const [invoices, setInvoices] = useState([]); // { id, name, month, year, bankId, imageUrl, manualItems[], total }

  // Budget
  const [budgets, setBudgets] = useState(DEFAULT_EXPENSE_CATS.slice(0, 8).map(c => ({ catId: c.id, fixed: 0, variable: 0 })));
  const [budgetMonth, setBudgetMonth] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`; });

  // Patrimônio: salary + accounts + cards + assets
  const [salary, setSalary] = useState(0);
  const [displayName, setDisplayName] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [cards, setCards] = useState([]);
  const [assets, setAssets] = useState([]);

  // Dashboard filter
  const [dashMonth, setDashMonth] = useState("all");

  // Modals / UI
  const [modalOpen, setModalOpen] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [dragOverInvoice, setDragOverInvoice] = useState(false);
  const [expandedInvoice, setExpandedInvoice] = useState(null);
  const [confirmClear, setConfirmClear] = useState(false);
  const [confirmLogout, setConfirmLogout] = useState(false);
  const inputRef = useRef(null);
  const invoiceInputRef = useRef(null);

  // ── Supabase auth session ──
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setAuthUser(session?.user ?? null);
      setAuthLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      setAuthUser(session?.user ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);

  // ── Load all data when user logs in ──
  useEffect(() => {
    if (!authUser?.id) return;
    const uid = authUser.id;
    async function loadAll() {
      const [txRes, invRes, accRes, cardRes, catRes, budRes, assetRes, profRes, rulesRes] = await Promise.all([
        supabase.from("transactions").select("*").eq("user_id", uid).order("date", { ascending: false }),
        supabase.from("invoices").select("*").eq("user_id", uid),
        supabase.from("accounts").select("*").eq("user_id", uid),
        supabase.from("cards").select("*").eq("user_id", uid),
        supabase.from("categories").select("*").eq("user_id", uid),
        supabase.from("budgets").select("*").eq("user_id", uid),
        supabase.from("assets").select("*").eq("user_id", uid),
        supabase.from("user_profiles").select("*").eq("user_id", uid).maybeSingle(),
        supabase.from("user_rules").select("*").eq("user_id", uid),
      ]);
      if (txRes.data?.length)    setTransactions(txRes.data.map(dbToTx));
      if (invRes.data?.length)   setInvoices(invRes.data.map(dbToInv));
      if (accRes.data?.length)   setAccounts(accRes.data);
      if (cardRes.data?.length)  setCards(cardRes.data);
      if (catRes.data?.length) {
        // Migrate: ensure all default cats exist, remove stale ones (outros_rec, old labels)
        const STALE_IDS = ["outros_rec", "lazer_streaming", "salario", "freelance", "investimentos_ret", "aluguel_rec", "vendas"]; // stale/removed ids
        const CANONICAL = [...DEFAULT_EXPENSE_CATS, ...DEFAULT_INCOME_CATS];
        const existingIds = catRes.data.map(c => c.id);
        const canonicalIds = CANONICAL.map(c => c.id);
        // Delete stale categories
        const toDelete = catRes.data.filter(c => STALE_IDS.includes(c.id));
        if (toDelete.length) {
          await supabase.from("categories").delete().eq("user_id", uid).in("id", toDelete.map(c => c.id));
        }
        // Insert missing canonical categories
        const toInsert = CANONICAL.filter(c => !existingIds.includes(c.id)).map(c => ({ ...c, user_id: uid }));
        if (toInsert.length) {
          await supabase.from("categories").insert(toInsert);
        }
        // Update labels/icons for existing ones that may have stale labels
        const toUpdate = CANONICAL.filter(c => {
          const existing = catRes.data.find(e => e.id === c.id);
          return existing && (existing.label !== c.label || existing.icon !== c.icon);
        });
        for (const c of toUpdate) {
          await supabase.from("categories").update({ label: c.label, icon: c.icon }).eq("user_id", uid).eq("id", c.id);
        }
        // Build final list: canonical order, keep user colors if customized
        const merged = CANONICAL.map(def => {
          const saved = catRes.data.find(c => c.id === def.id);
          return saved ? { ...def, color: saved.color } : def;
        });
        setCategories(merged);
      } else {
        // First login — seed default categories
        const rows = [...DEFAULT_EXPENSE_CATS, ...DEFAULT_INCOME_CATS].map(c => ({ ...c, user_id: uid }));
        await supabase.from("categories").insert(rows);
        setCategories([...DEFAULT_EXPENSE_CATS, ...DEFAULT_INCOME_CATS]);
      }
      if (budRes.data?.length)   setBudgets(budRes.data.map(b => ({ catId: b.cat_id, fixed: Number(b.fixed||0), variable: Number(b.variable||0) })));
      if (assetRes.data?.length) setAssets(assetRes.data);
      if (rulesRes.data?.length) {
        const rules = {};
        rulesRes.data.forEach(r => { rules[r.keyword] = r.cat_id; });
        setUserRules(rules);
      }
      if (profRes.data) {
        setSalary(Number(profRes.data.salary || 0));
        if (profRes.data.display_name) setDisplayName(profRes.data.display_name);
      }
    }
    loadAll();
  }, [authUser?.id]);

  // Detect mobile
  useEffect(() => {
    const check = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) setSidebarOpen(false);
      else setSidebarOpen(true);
    };
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const allCats = categories;
  const expCats = categories.filter(c => c.type === "expense");
  const incCats = categories.filter(c => c.type === "income");

  // ─── Categorize with user learned rules applied on top of default KW ───
  const categorizeWithRules = useCallback((desc) => {
    if (!desc) return null;
    const d = desc.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    // User rules take priority — match any keyword fragment
    for (const [kw, catId] of Object.entries(userRules)) {
      const kwN = kw.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
      if (d.includes(kwN)) return catId;
    }
    return categorize(desc);
  }, [userRules]);

  // ─── Save a learned rule when user corrects a category ───
  const saveRule = useCallback((description, catId) => {
    if (!description || !catId || !authUser) return;
    // Use a normalized key: lowercase, no accents, trimmed to first ~40 chars of meaningful part
    const key = description.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim().slice(0, 60);
    if (!key) return;
    setUserRules(prev => ({ ...prev, [key]: catId }));
    supabase.from("user_rules").upsert({ user_id: authUser.id, keyword: key, cat_id: catId, updated_at: new Date().toISOString() }, { onConflict: "user_id,keyword" });
  }, [authUser]);

  // ─── File handling: CSV/PDF/OFX/XLS → parse then open review screen ───
  const handleFiles = useCallback(async (fileList, importMonth) => {
    const accepted = Array.from(fileList).filter(f =>
      ["csv","xls","xlsx"].includes(f.name.split(".").pop().toLowerCase())
    );
    if (!accepted.length) return;
    const file = accepted[0];
    try {
      const r = await processFile(file);
      if (r.transactions.length > 0) {
        const items = r.transactions.map(t => ({
          ...t,
          needsReview: !categorizeWithRules(t.description),
          category: categorizeWithRules(t.description) || t.category || null,
        }));
        setReviewDraft({
          type: "structured",
          items,
          fileName: file.name,
          imageUrl: null,
          importMonth: importMonth || null,
        });
        setPage("review");
      }
    } catch (e) { console.error(e); }
  }, []);

  // ─── Image invoice: extract via Claude Vision then open review screen ───
  const handleInvoiceImages = useCallback(async (fileList, invoiceConfig) => {
    const accepted = Array.from(fileList).filter(f =>
      ["png","jpg","jpeg"].includes(f.name.split(".").pop().toLowerCase())
    );
    if (!accepted.length) return;

    const file = accepted[0];
    const url = URL.createObjectURL(file);
    const apiKey = localStorage.getItem("anthropic_key") || window.__ANTHROPIC_KEY__ || "";

    // Open review immediately with loading state
    setReviewDraft({
      type: "image",
      items: [],
      fileName: invoiceConfig?.name || file.name.replace(/\.[^.]+$/, ""),
      imageUrl: url,
      invoiceConfig,
      importMonth: invoiceConfig?.month || null,
      extracting: true,
      extractError: null,
    });
    setPage("review");

    if (!apiKey) {
      setReviewDraft(d => d ? { ...d, extracting: false, extractError: "Chave Anthropic não configurada. Adicione os itens manualmente." } : d);
      return;
    }

    try {
      const b64 = await fileToBase64(file);
      const mediaType = file.type || "image/jpeg";
      const response = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": apiKey,
          "anthropic-version": "2023-06-01",
          "anthropic-dangerous-direct-browser-access": "true",
        },
        body: JSON.stringify({
          model: "claude-opus-4-6",
          max_tokens: 3000,
          messages: [{
            role: "user",
            content: [
              { type: "image", source: { type: "base64", media_type: mediaType, data: b64 } },
              { type: "text", text: `Esta é uma fatura de cartão de crédito brasileiro. Extraia TODOS os lançamentos/compras visíveis.

Retorne APENAS um JSON array no formato:
[{
  "descricao": "nome do estabelecimento exatamente como aparece",
  "valor": 99.90,
  "data": "DD/MM/AAAA ou null",
  "parcela_atual": 2,
  "parcela_total": 12,
  "pagamento_fatura": false
}]

Regras OBRIGATÓRIAS:
- TODOS os lançamentos são GASTOS (type expense) — NUNCA marque como receita
- Se a linha for "Pagamento", "Pgto Fatura", "Crédito" ou "Estorno" → set "pagamento_fatura": true
- Para compras parceladas (ex: "LOJA 2/12", "PARCELA 3 DE 10") → preencha parcela_atual e parcela_total
- Para compras à vista → parcela_atual: 1, parcela_total: 1
- valor deve ser número positivo
- IGNORE linhas de totais, subtotais, cabeçalhos
- Responda SOMENTE com o JSON, sem nenhum texto adicional` }
            ]
          }]
        })
      });
      const data = await response.json();
      if (data.error) throw new Error(data.error.message);
      const text = (data.content || []).map(c => c.text || "").join("");
      const clean = text.replace(/```json|```/g, "").trim();
      let rawItems = [];
      try { rawItems = JSON.parse(clean); } catch {}
      if (!Array.isArray(rawItems)) rawItems = [];

      const expCatsLocal = categories.filter(c => c.type === "expense");
      const items = rawItems.map((item, i) => {
        const isPayment = !!item.pagamento_fatura || isPaymentTransaction(item.descricao);
        if (isPayment) return null; // skip payment lines entirely from review
        const catId = categorize(item.descricao) || "outros_desp";
        const d = item.data ? parseDate(item.data) : null;
        const parcAtual = parseInt(item.parcela_atual) || 1;
        const parcTotal = parseInt(item.parcela_total) || 1;
        const isInstallment = parcTotal > 1;
        // Build readable description
        let desc = item.descricao || "Sem descrição";
        return {
          id: `item-${Date.now()}-${i}`,
          description: desc,
          value: Math.abs(parseFloat(item.valor) || 0),
          category: catId,
          type: "expense",
          date: d,
          source: file.name,
          needsReview: !categorize(item.descricao),
          isPayment: false,
          installment: isInstallment ? { current: parcAtual, total: parcTotal } : null,
        };
      }).filter(x => x && x.value > 0);

      setReviewDraft(d => d ? { ...d, items, extracting: false } : d);
    } catch (err) {
      setReviewDraft(d => d ? { ...d, extracting: false, extractError: `Erro ao extrair: ${err.message}` } : d);
    }
  }, [categories]);

  const updateCat = (id, cat) => {
    const catObj = allCats.find(c => c.id === cat);
    const isIncome = catObj?.type === "income";
    setTransactions(p => p.map(t => t.id === id ? { ...t, category: cat, needsReview: false, type: isIncome ? "income" : "expense" } : t));
  };

  // ─── Filtered transactions (excluding payments) ───
  const realTransactions = useMemo(() =>
    transactions.filter(t => !t.isPayment), [transactions]);

  // ─── Dashboard month filter ───
  const filteredTxns = useMemo(() => {
    if (dashMonth === "all") return realTransactions;
    const [y, m] = dashMonth.split("-");
    return realTransactions.filter(t => t.date &&
      t.date.getFullYear() === +y && t.date.getMonth() === +m - 1);
  }, [realTransactions, dashMonth]);

  const totalExpenses = filteredTxns.filter(t => t.type === "expense").reduce((s, t) => s + t.value, 0);
  const totalIncome = filteredTxns.filter(t => t.type === "income").reduce((s, t) => s + t.value, 0);
  const balance = totalIncome - totalExpenses;
  const totalAccounts = accounts.reduce((s, a) => s + a.balance, 0);
  const totalAssets = assets.reduce((s, a) => s + a.value, 0);
  const othersCount = transactions.filter(t => t.needsReview).length;

  // Available months for filter
  const availableMonths = useMemo(() => {
    const months = new Set();
    realTransactions.forEach(t => {
      if (t.date) {
        const key = `${t.date.getFullYear()}-${String(t.date.getMonth()+1).padStart(2,"0")}`;
        months.add(key);
      }
    });
    return Array.from(months).sort().reverse();
  }, [realTransactions]);

  // Category analysis
  const catAnalysis = useMemo(() => {
    const byC = {};
    allCats.forEach(c => { byC[c.id] = { ...c, total: 0, count: 0 }; });
    filteredTxns.forEach(t => { if (byC[t.category]) { byC[t.category].total += t.value; byC[t.category].count++; } });
    return Object.values(byC).filter(c => c.total > 0).sort((a, b) => b.total - a.total);
  }, [filteredTxns, allCats]);

  const expenseByCategory = catAnalysis.filter(c => c.type === "expense");
  const pieData = expenseByCategory.map(c => ({ name: c.label, value: c.total, color: c.color, icon: c.icon, pct: totalExpenses > 0 ? c.total / totalExpenses : 0 }));

  // Monthly data (all time, for bar chart)
  const monthlyData = useMemo(() => {
    const byM = {};
    realTransactions.forEach(t => {
      if (t.date) {
        const k = `${t.date.getFullYear()}-${String(t.date.getMonth()+1).padStart(2,"0")}`;
        if (!byM[k]) byM[k] = { income: 0, expense: 0 };
        byM[k][t.type] += t.value;
      }
    });
    return Object.entries(byM).sort().map(([k, v]) => {
      const [y, m] = k.split("-");
      return { key: k, name: `${MONTHS[+m-1]}/${y.slice(2)}`, Receita: Math.round(v.income), Despesa: Math.round(v.expense), Saldo: Math.round(v.income - v.expense) };
    });
  }, [realTransactions]);

  // Budget comparison for selected month
  const budgetComparison = useMemo(() => {
    return budgets.map(b => {
      const cat = allCats.find(c => c.id === b.catId);
      const spent = realTransactions.filter(t => t.category === b.catId && t.date &&
        `${t.date.getFullYear()}-${String(t.date.getMonth()+1).padStart(2,"0")}` === budgetMonth)
        .reduce((s, t) => s + t.value, 0);
      const planned = (b.fixed||0) + (b.variable||0);
      return { ...b, cat, spent, planned, pct: planned > 0 ? spent / planned : 0 };
    });
  }, [budgets, realTransactions, budgetMonth, allCats]);

  const navigate = (p) => {
    setPage(p);
    if (isMobile) setSidebarOpen(false);
  };

  const clearAllData = async () => {
    const uid = authUser?.id;
    setTransactions([]);
    setInvoices([]);
    setAccounts([]);
    setCards([]);
    setAssets([]);
    setSalary(0);
    setBudgets(DEFAULT_EXPENSE_CATS.slice(0, 8).map(c => ({ catId: c.id, fixed: 0, variable: 0 })));
    if (uid) {
      await Promise.all([
        supabase.from("transactions").delete().eq("user_id", uid),
        supabase.from("invoices").delete().eq("user_id", uid),
        supabase.from("accounts").delete().eq("user_id", uid),
        supabase.from("cards").delete().eq("user_id", uid),
        supabase.from("assets").delete().eq("user_id", uid),
        supabase.from("budgets").delete().eq("user_id", uid),
        supabase.from("user_profiles").upsert({ user_id: uid, salary: 0, updated_at: new Date().toISOString() }),
      ]);
    }
    setDashMonth("all");
    setConfirmClear(false);
  };

  /* ══════════════════════════════════════════
     NAV
     ══════════════════════════════════════════ */
  const NAV = [
    { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
    { id: "faturas", label: "Faturas", icon: FileText },
    { id: "patrimonio", label: "Patrimônio", icon: Banknote },
    { id: "orcamento", label: "Orçamento", icon: Target },
    { id: "categorias", label: "Categorias", icon: Tag },
  ];

  /* ══════════════════════════════════════════
     RENDER
     ══════════════════════════════════════════ */
  if (authLoading) return <div style={{ display:"flex",alignItems:"center",justifyContent:"center",height:"100vh",fontFamily:"'SuisseIntl',sans-serif",color:"#9CA3AF",fontSize:14 }}>Carregando...</div>;
  if (!authUser) return <LandingPage onLogin={(user) => setAuthUser(user)} />;

  return (
    <div style={S.app}>
      {/* Mobile overlay */}
      {isMobile && sidebarOpen && <div style={S.overlay} onClick={() => setSidebarOpen(false)}/>}

      {/* Mobile topbar */}
      <div style={S.topbar(isMobile)}>
        <button onClick={() => setSidebarOpen(o => !o)} style={{ ...S.btnSm, background:"#F3F4F6", color:"#374151", padding:8 }}>
          <Menu size={18}/>
        </button>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <img src="/logo-green.png" alt="Radar de Gastos" style={{ width:34, height:34, objectFit:"contain" }}/>
          <span style={{ fontSize:22,fontWeight:400,color:BRAND.green,letterSpacing:"-0.03em",fontFamily:"'LyonDisplay',Georgia,serif" }}>Radar de Gastos</span>
        </div>
      </div>

      {/* SIDEBAR */}
      <div style={S.sidebar(sidebarOpen, isMobile)}>
        <div style={{ display:"flex", alignItems:"center", justifyContent: sidebarOpen ? "space-between" : "center", marginBottom: 24, minHeight: 40, paddingTop: 8 }}>
          {sidebarOpen && <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <img src="/logo-lime.png" alt="Radar de Gastos" style={{ width:34, height:34, objectFit:"contain", flexShrink:0 }}/>
            <span style={{ fontSize:22,fontWeight:400,color:"#FFFFFF",letterSpacing:"-0.03em",fontFamily:"'LyonDisplay',Georgia,serif",whiteSpace:"nowrap" }}>Radar de Gastos</span>
          </div>}
          {!isMobile && (
            <button onClick={() => setSidebarOpen(o => !o)}
              style={{ background:"none", border:"none", cursor:"pointer", color:"rgba(255,255,255,0.45)", display:"flex", alignItems:"center", justifyContent:"center", padding:4, borderRadius:6, flexShrink:0 }}
              title={sidebarOpen ? "Recolher" : "Expandir"}>
              {sidebarOpen ? <ChevronLeft size={16}/> : <ChevronRight size={16}/>}
            </button>
          )}
        </div>

        {NAV.map(n => (
          <div key={n.id} style={S.navItem(page === n.id)} onClick={() => navigate(n.id)} title={!sidebarOpen ? n.label : ""}>
            <n.icon size={18} style={{ flexShrink: 0 }}/>
            {sidebarOpen && <span>{n.label}</span>}
          </div>
        ))}

        {/* Logout */}
        <div style={{ borderTop: "1px solid rgba(255,255,255,0.12)", marginTop: 4, paddingTop: 4 }}>
          <div style={{ ...S.navItem(false), color: "#FFFFFF", fontWeight: 700 }}
            onClick={() => setConfirmLogout(true)}
            title={!sidebarOpen ? "Sair" : ""}>
            <LogOut size={18} style={{ flexShrink: 0 }}/>
            {sidebarOpen && <span style={{ fontWeight:700 }}>Sair</span>}
          </div>
        </div>
      </div>

      {/* MAIN */}
      <div style={S.main(sidebarOpen, isMobile)}>

        {/* ═══════════════════════════════════
            REVIEW (import confirmation screen)
            ═══════════════════════════════════ */}
        {page === "review" && reviewDraft && (
          <ReviewPage
            draft={reviewDraft}
            setDraft={setReviewDraft}
            categories={categories}
            accounts={accounts}
            onLearnRule={saveRule}
            onConfirm={(confirmedItems, month, year, bankId, invoiceName) => {
              if (reviewDraft.type === "structured") {
                const importId = `imp-${Date.now()}`;
                const withImport = confirmedItems.map(t => ({ ...t, importId, importName: invoiceName || reviewDraft.fileName }));
                setTransactions(p => [...p, ...withImport]);
                supabase.from("transactions").insert(withImport.map(t => txToDb(t, authUser.id)));
              } else {
                const invoiceId = `inv-${Date.now()}`;
                const manualItems = confirmedItems.map(t => ({
                  id: t.id,
                  description: t.description,
                  value: t.value,
                  category: t.category,
                  catLabel: categories.find(c => c.id === t.category)?.label || "",
                  catIcon: categories.find(c => c.id === t.category)?.icon || "",
                  catColor: categories.find(c => c.id === t.category)?.color || "#ccc",
                  dateStr: t.date ? t.date.toLocaleDateString("pt-BR") : null,
                }));
                const newInv = { id: invoiceId, name: invoiceName || reviewDraft.fileName, month, year, bankId, imageUrl: reviewDraft.imageUrl, manualItems, total: manualItems.reduce((s, x) => s + x.value, 0), extracting: false, extractError: null };
                setInvoices(p => [...p, newInv]);
                supabase.from("invoices").insert([invToDb(newInv, authUser.id)]);
              }
              setReviewDraft(null);
              setPage("faturas");
            }}
            onCancel={() => { setReviewDraft(null); setPage("faturas"); }}
          />
        )}

        {/* ═══════════════════════════════════
            DASHBOARD
            ═══════════════════════════════════ */}
        {page === "dashboard" && (() => {
          const rawName = displayName || authUser?.email?.split("@")[0] || "você";
          const greetName = rawName.charAt(0).toUpperCase() + rawName.slice(1);

          // Installments calc
          const now2 = new Date();
          const installmentTxns = realTransactions.filter(t => t.installment && t.installment.total > 1 && t.date);
          const futureByMonth = {};
          installmentTxns.forEach(t => {
            const remaining = t.installment.total - t.installment.current;
            if (remaining <= 0) return;
            for (let i = 1; i <= remaining; i++) {
              const fd = new Date(t.date.getFullYear(), t.date.getMonth() + i, 1);
              const key = `${fd.getFullYear()}-${String(fd.getMonth()+1).padStart(2,"0")}`;
              if (!futureByMonth[key]) futureByMonth[key] = { total: 0, items: [] };
              futureByMonth[key].total += t.value;
              futureByMonth[key].items.push({ desc: t.description, value: t.value, installment: `${t.installment.current + i}/${t.installment.total}` });
            }
          });
          const futureMonths = Object.entries(futureByMonth)
            .filter(([k]) => k > `${now2.getFullYear()}-${String(now2.getMonth()+1).padStart(2,"0")}`)
            .sort(([a],[b]) => a.localeCompare(b)).slice(0, 6);

          // Recent transactions (last 5)
          const recentTxns = [...filteredTxns].sort((a,b) => (b.date||0) - (a.date||0)).slice(0, 5);

          // Day-of-week avg spend
          const DOW = ["Dom","Seg","Ter","Qua","Qui","Sex","Sáb"];
          const dowTotals = [0,0,0,0,0,0,0];
          const dowCounts = [0,0,0,0,0,0,0];
          filteredTxns.filter(t=>t.type==="expense"&&t.date).forEach(t => {
            const d = t.date.getDay();
            dowTotals[d] += t.value;
            dowCounts[d]++;
          });
          const dowData = DOW.map((name,i) => ({ name, avg: dowCounts[i]>0 ? Math.round(dowTotals[i]/dowCounts[i]) : 0, total: Math.round(dowTotals[i]) }));

          // Monthly balance evolution
          const balanceEvolution = monthlyData.map((m,i,arr) => {
            const cum = arr.slice(0,i+1).reduce((s,x)=>s+(x.Receita-x.Despesa),0);
            return { ...m, Acumulado: Math.round(cum) };
          });

          // Expense vs income ratio by month
          const expIncomeRatio = monthlyData.map(m => ({
            ...m,
            "% Gastos": m.Receita>0 ? Math.round((m.Despesa/m.Receita)*100) : 0,
          }));

          // Top categories % of income
          const catVsIncome = expenseByCategory.slice(0,8).map(c => ({
            name: c.label, icon: c.icon, color: c.color, total: c.total,
            pctIncome: totalIncome > 0 ? (c.total/totalIncome)*100 : 0,
            pctExpense: totalExpenses > 0 ? (c.total/totalExpenses)*100 : 0,
          }));

          return (
          <div>
            {/* ── Top header ── */}
            <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:28,flexWrap:"wrap",gap:12 }}>
              <div>
                <div style={{ fontFamily:"'LyonDisplay',Georgia,serif",fontSize:42,fontWeight:400,color:BRAND.text,letterSpacing:"-0.03em",lineHeight:1.1 }}>Olá, {greetName}! 👋</div>
                <div style={{ fontSize:13,color:BRAND.muted,marginTop:6 }}>Visão geral das suas finanças</div>
              </div>
              <div style={{ display:"flex",gap:8,alignItems:"center" }}>
                <select value={dashMonth} onChange={e => setDashMonth(e.target.value)} style={{ ...S.sel, fontSize:12, padding:"6px 12px" }}>
                  <option value="all">Todo o período</option>
                  {availableMonths.map(m => { const [y,mo]=m.split("-"); return <option key={m} value={m}>{MONTHS_FULL[+mo-1]} {y}</option>; })}
                </select>
                <button style={{ ...S.btn2, color:"#DC2626", borderColor:"#FCA5A5", fontSize:12, padding:"6px 12px" }} onClick={() => setConfirmClear(true)}>
                  <RotateCcw size={13}/> Limpar
                </button>
              </div>
            </div>

            {filteredTxns.length === 0 ? (
              <div style={{ ...S.card, textAlign:"center", padding:56, border:"2px dashed "+BRAND.border }}>
                <div style={{ width:64,height:64,borderRadius:20,background:BRAND.bg,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 16px" }}>
                  <Upload size={28} color={BRAND.muted} strokeWidth={1.5}/>
                </div>
                <div style={{ fontSize:16,fontWeight:600,color:BRAND.text,marginBottom:6 }}>Nenhum dado ainda</div>
                <div style={{ fontSize:13,color:BRAND.muted,marginBottom:20 }}>Importe suas faturas para começar a analisar</div>
                <button style={S.btn1} onClick={() => navigate("faturas")}>Importar faturas <ChevronRight size={14}/></button>
              </div>
            ) : (
              <>
                {/* ── KPI row ── */}
                <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))", gap:12, marginBottom:20 }}>
                  {[
                    { l:"RECEITAS", v:fmt(totalIncome), c:"#22C55E", bg:"#DCFCE7", ic:<TrendingUp size={16}/>, sub: dashMonth==="all"?"acumulado":"este mês" },
                    { l:"DESPESAS", v:fmt(totalExpenses), c:"#EF4444", bg:"#FEE2E2", ic:<TrendingDown size={16}/>, sub: dashMonth==="all"?"acumulado":"este mês" },
                    { l:"SALDO", v:fmt(balance), c:balance>=0?BRAND.green:"#EF4444", bg:balance>=0?"#D1FAE5":"#FEE2E2", ic:<DollarSign size={16}/>, sub:balance>=0?"positivo":"negativo" },
                    { l:"EM CONTAS", v:fmt(totalAccounts), c:"#8B5CF6", bg:"#F3E8FF", ic:<Wallet size={16}/>, sub:"patrimônio líquido" },
                    { l:"INVESTIDO", v:fmt(totalAssets), c:"#F59E0B", bg:"#FEF3C7", ic:<Briefcase size={16}/>, sub:"total aplicado" },
                  ].map((m,i) => (
                    <div key={i} style={{ background:BRAND.surface, borderRadius:14, padding:"16px 16px", border:`1px solid ${BRAND.border}`, boxShadow:"0 1px 3px rgba(0,0,0,0.04)" }}>
                      <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10 }}>
                        <span style={{ fontSize:10,fontWeight:700,color:BRAND.muted,letterSpacing:"0.06em" }}>{m.l}</span>
                        <div style={{ width:30,height:30,borderRadius:8,background:m.bg,display:"flex",alignItems:"center",justifyContent:"center",color:m.c }}>{m.ic}</div>
                      </div>
                      <div style={{ fontSize:22,fontWeight:800,color:BRAND.text,letterSpacing:"-0.03em",lineHeight:1 }}>{m.v}</div>
                      <div style={{ fontSize:11,color:BRAND.muted,marginTop:4 }}>{m.sub}</div>
                    </div>
                  ))}
                </div>

                {/* ── Main row: big chart + sidebar ── */}
                <div style={{ display:"grid", gridTemplateColumns:"minmax(0,1fr) 300px", gap:16, marginBottom:16, alignItems:"start" }}>

                  {/* Cash flow area chart */}
                  <div style={{ ...S.card, marginBottom:0 }}>
                    <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20 }}>
                      <div>
                        <div style={{ fontSize:11,fontWeight:700,color:BRAND.muted,letterSpacing:"0.06em",textTransform:"uppercase" }}>FLUXO MENSAL</div>
                        <div style={{ fontSize:22,fontWeight:800,color:BRAND.text,letterSpacing:"-0.03em",marginTop:2 }}>{fmt(balance)}</div>
                        <div style={{ fontSize:12,color:balance>=0?BRAND.green:"#EF4444",fontWeight:600 }}>{balance>=0?"▲":"▼"} Saldo acumulado</div>
                      </div>
                    </div>
                    <div style={{ height:230 }}>
                      <ResponsiveContainer>
                        <ComposedChart data={monthlyData} margin={{ top:5,right:5,left:0,bottom:0 }}>
                          <defs>
                            <linearGradient id="gradReceita" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#22C55E" stopOpacity={0.18}/>
                              <stop offset="95%" stopColor="#22C55E" stopOpacity={0}/>
                            </linearGradient>
                            <linearGradient id="gradDespesa" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#EF4444" stopOpacity={0.13}/>
                              <stop offset="95%" stopColor="#EF4444" stopOpacity={0}/>
                            </linearGradient>
                            <linearGradient id="gradSaldo" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor={BRAND.green} stopOpacity={0.25}/>
                              <stop offset="95%" stopColor={BRAND.green} stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={BRAND.border}/>
                          <XAxis dataKey="name" tick={{ fontSize:10,fill:BRAND.muted }} axisLine={false} tickLine={false}/>
                          <YAxis tick={{ fontSize:9,fill:BRAND.muted }} axisLine={false} tickLine={false} tickFormatter={v => v>=1000?`${(v/1000).toFixed(0)}k`:v} width={38}/>
                          <Tooltip content={<TT/>} cursor={{ stroke:BRAND.border, strokeWidth:1 }}/>
                          <Legend iconType="circle" iconSize={7} wrapperStyle={{ fontSize:11,paddingTop:12 }}/>
                          <Area type="monotone" dataKey="Receita" fill="url(#gradReceita)" stroke="#22C55E" strokeWidth={2.5} dot={false} activeDot={{ r:5,fill:"#22C55E",stroke:"#fff",strokeWidth:2 }}/>
                          <Area type="monotone" dataKey="Despesa" fill="url(#gradDespesa)" stroke="#EF4444" strokeWidth={2.5} dot={false} activeDot={{ r:5,fill:"#EF4444",stroke:"#fff",strokeWidth:2 }}/>
                          <Line type="monotone" dataKey="Saldo" stroke={BRAND.green} strokeWidth={2} dot={false} strokeDasharray="5 3" activeDot={{ r:5,fill:BRAND.green,stroke:"#fff",strokeWidth:2 }}/>
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* Right sidebar: recent txns */}
                  <div style={{ ...S.card, marginBottom:0 }}>
                    <div style={{ fontSize:11,fontWeight:700,color:BRAND.muted,letterSpacing:"0.06em",textTransform:"uppercase",marginBottom:14 }}>ÚLTIMAS TRANSAÇÕES</div>
                    {recentTxns.length === 0 ? (
                      <div style={{ color:BRAND.muted,fontSize:12,padding:"20px 0",textAlign:"center" }}>Sem transações</div>
                    ) : recentTxns.map((t,i) => {
                      const cat = allCats.find(c=>c.id===t.category);
                      return (
                        <div key={t.id} style={{ display:"flex",alignItems:"center",gap:10,padding:"9px 0",borderBottom:i<recentTxns.length-1?`1px solid ${BRAND.border}`:"none" }}>
                          <div style={{ width:34,height:34,borderRadius:10,background:(cat?.color||BRAND.green)+"18",display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,flexShrink:0 }}>{cat?.icon||"•"}</div>
                          <div style={{ flex:1,minWidth:0 }}>
                            <div style={{ fontSize:12,fontWeight:600,color:BRAND.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>{t.description}</div>
                            <div style={{ fontSize:10,color:BRAND.muted }}>{cat?.label||"—"} {t.date?`· ${t.date.toLocaleDateString("pt-BR",{day:"2-digit",month:"short"})}`:""}</div>
                          </div>
                          <div style={{ fontSize:13,fontWeight:700,color:t.type==="income"?"#22C55E":"#EF4444",whiteSpace:"nowrap" }}>{t.type==="income"?"+":"-"}{fmt(t.value)}</div>
                        </div>
                      );
                    })}
                    <button style={{ ...S.btn2, width:"100%", justifyContent:"center", marginTop:12, fontSize:12 }} onClick={() => navigate("faturas")}>
                      Ver tudo <ChevronRight size={13}/>
                    </button>
                  </div>
                </div>

                {/* ── Bottom row: donut + top cats + top gastos ── */}
                <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(240px,1fr))", gap:16, marginBottom:16, alignItems:"start" }}>

                  {/* Donut chart */}
                  <div style={{ ...S.card, marginBottom:0 }}>
                    <div style={{ fontSize:11,fontWeight:700,color:BRAND.muted,letterSpacing:"0.06em",textTransform:"uppercase",marginBottom:4 }}>CATEGORIAS</div>
                    {pieData.length > 0 ? (
                      <>
                        <div style={{ height:180, position:"relative" }}>
                          <ResponsiveContainer>
                            <PieChart>
                              <Pie data={pieData} cx="50%" cy="50%" innerRadius="55%" outerRadius="85%" dataKey="value" paddingAngle={3} stroke="none" startAngle={90} endAngle={-270}>
                                {pieData.map((d,i) => <Cell key={i} fill={d.color}/>)}
                              </Pie>
                              <Tooltip content={<DTT/>}/>
                            </PieChart>
                          </ResponsiveContainer>
                          {/* Center label */}
                          <div style={{ position:"absolute",top:"50%",left:"50%",transform:"translate(-50%,-50%)",textAlign:"center",pointerEvents:"none" }}>
                            <div style={{ fontSize:11,color:BRAND.muted,fontWeight:500 }}>Total</div>
                            <div style={{ fontSize:14,fontWeight:800,color:BRAND.text,letterSpacing:"-0.02em" }}>{fmt(totalExpenses)}</div>
                          </div>
                        </div>
                        <div style={{ display:"flex",flexDirection:"column",gap:4,marginTop:8 }}>
                          {pieData.slice(0,4).map(d => (
                            <div key={d.name} style={{ display:"flex",alignItems:"center",gap:6,fontSize:11 }}>
                              <span style={{ width:8,height:8,borderRadius:2,background:d.color,flexShrink:0,display:"inline-block" }}/>
                              <span style={{ flex:1,color:BRAND.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>{d.name}</span>
                              <span style={{ color:BRAND.muted,fontWeight:600 }}>{(d.pct*100).toFixed(0)}%</span>
                            </div>
                          ))}
                        </div>
                      </>
                    ) : <div style={{ color:BRAND.muted,fontSize:12,padding:"40px 0",textAlign:"center" }}>Sem despesas</div>}
                  </div>

                  {/* Top categories bar */}
                  <div style={{ ...S.card, marginBottom:0 }}>
                    <div style={{ fontSize:11,fontWeight:700,color:BRAND.muted,letterSpacing:"0.06em",textTransform:"uppercase",marginBottom:14 }}>GASTOS POR CATEGORIA</div>
                    {expenseByCategory.slice(0,6).map((c,i) => {
                      const pct = totalExpenses > 0 ? c.total / totalExpenses : 0;
                      return (
                        <div key={c.id} style={{ marginBottom:12 }}>
                          <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:5 }}>
                            <div style={{ display:"flex",alignItems:"center",gap:6 }}>
                              <span style={{ fontSize:15 }}>{c.icon}</span>
                              <span style={{ fontSize:12,fontWeight:500,color:BRAND.text }}>{c.label}</span>
                            </div>
                            <div style={{ textAlign:"right" }}>
                              <span style={{ fontSize:12,fontWeight:700,color:BRAND.text }}>{fmt(c.total)}</span>
                              <span style={{ fontSize:10,color:BRAND.muted,marginLeft:5 }}>{(pct*100).toFixed(0)}%</span>
                            </div>
                          </div>
                          <div style={{ height:5,background:BRAND.border,borderRadius:99,overflow:"hidden" }}>
                            <div style={{ height:"100%",width:`${pct*100}%`,background:c.color,borderRadius:99,transition:"width 0.6s cubic-bezier(.4,0,.2,1)" }}/>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Top 5 gastos */}
                  <div style={{ ...S.card, marginBottom:0 }}>
                    <div style={{ fontSize:11,fontWeight:700,color:BRAND.muted,letterSpacing:"0.06em",textTransform:"uppercase",marginBottom:14 }}>MAIORES GASTOS</div>
                    {[...filteredTxns].filter(t=>t.type==="expense").sort((a,b)=>b.value-a.value).slice(0,5).map((t,i) => {
                      const cat = allCats.find(c=>c.id===t.category);
                      const rankColors = [BRAND.green,"#22C55E","#86EFAC","#BBF7D0","#D1FAE5"];
                      return (
                        <div key={t.id} style={{ display:"flex",alignItems:"center",gap:10,padding:"8px 0",borderBottom:i<4?`1px solid ${BRAND.border}`:"none" }}>
                          <div style={{ width:22,height:22,borderRadius:6,background:rankColors[i],display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:800,color:i<2?"#fff":BRAND.green,flexShrink:0 }}>{i+1}</div>
                          <div style={{ flex:1,minWidth:0 }}>
                            <div style={{ fontSize:12,fontWeight:500,color:BRAND.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>{t.description}</div>
                            <div style={{ fontSize:10,color:BRAND.muted }}>{cat?.icon} {cat?.label||"—"}</div>
                          </div>
                          <div style={{ fontSize:13,fontWeight:700,color:BRAND.text,whiteSpace:"nowrap" }}>{fmt(t.value)}</div>
                        </div>
                      );
                    })}
                    {filteredTxns.filter(t=>t.type==="expense").length === 0 && <div style={{ color:BRAND.muted,fontSize:12,padding:"20px 0",textAlign:"center" }}>Sem transações</div>}
                  </div>
                </div>

                {/* ── Row 3: Day-of-week avg + % gastos vs receita ── */}
                <div style={{ display:"grid", gridTemplateColumns:"minmax(0,1fr) minmax(0,1fr)", gap:16, marginBottom:16, alignItems:"start" }}>

                  {/* Gasto médio por dia da semana */}
                  <div style={{ ...S.card, marginBottom:0 }}>
                    <div style={{ marginBottom:16 }}>
                      <div style={{ fontSize:11,fontWeight:700,color:BRAND.muted,letterSpacing:"0.06em",textTransform:"uppercase" }}>GASTO MÉDIO POR DIA DA SEMANA</div>
                      <div style={{ fontFamily:"'LyonDisplay',Georgia,serif",fontSize:18,fontWeight:400,color:BRAND.text,marginTop:4,letterSpacing:"-0.02em" }}>
                        {dowData.reduce((a,b)=>b.avg>a.avg?b:a,dowData[0])?.name || "—"} é o dia com mais gastos
                      </div>
                    </div>
                    <div style={{ height:180 }}>
                      <ResponsiveContainer>
                        <BarChart data={dowData} margin={{ top:4,right:4,left:-20,bottom:0 }} barCategoryGap="28%">
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={BRAND.border}/>
                          <XAxis dataKey="name" tick={{ fontSize:11,fill:BRAND.muted }} axisLine={false} tickLine={false}/>
                          <YAxis tick={{ fontSize:9,fill:BRAND.muted }} axisLine={false} tickLine={false} tickFormatter={v=>v>=1000?`${(v/1000).toFixed(0)}k`:v} width={36}/>
                          <Tooltip formatter={(v)=>[fmt(v),"Média"]} labelStyle={{ color:BRAND.text,fontWeight:600 }} contentStyle={{ background:BRAND.surface,border:`1px solid ${BRAND.border}`,borderRadius:10,fontSize:12 }}/>
                          <Bar dataKey="avg" radius={[6,6,0,0]}>
                            {dowData.map((d,i) => {
                              const maxAvg = Math.max(...dowData.map(x=>x.avg));
                              return <Cell key={i} fill={d.avg===maxAvg ? BRAND.green : BRAND.green+"66"}/>;
                            })}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* % gastos vs receita por mês */}
                  <div style={{ ...S.card, marginBottom:0 }}>
                    <div style={{ marginBottom:16 }}>
                      <div style={{ fontSize:11,fontWeight:700,color:BRAND.muted,letterSpacing:"0.06em",textTransform:"uppercase" }}>DESPESAS VS RECEITA</div>
                      <div style={{ fontFamily:"'LyonDisplay',Georgia,serif",fontSize:18,fontWeight:400,color:BRAND.text,marginTop:4,letterSpacing:"-0.02em" }}>
                        % dos gastos em relação à receita
                      </div>
                    </div>
                    <div style={{ height:180 }}>
                      <ResponsiveContainer>
                        <ComposedChart data={expIncomeRatio} margin={{ top:4,right:4,left:-20,bottom:0 }}>
                          <defs>
                            <linearGradient id="gradRatio" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#EF4444" stopOpacity={0.18}/>
                              <stop offset="95%" stopColor="#EF4444" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={BRAND.border}/>
                          <XAxis dataKey="name" tick={{ fontSize:10,fill:BRAND.muted }} axisLine={false} tickLine={false}/>
                          <YAxis tick={{ fontSize:9,fill:BRAND.muted }} axisLine={false} tickLine={false} tickFormatter={v=>`${v}%`} width={36}/>
                          <ReferenceLine y={100} stroke="#EF4444" strokeDasharray="4 3" strokeWidth={1.5}/>
                          <Tooltip formatter={(v,n)=>[`${v}%`,n]} labelStyle={{ color:BRAND.text,fontWeight:600 }} contentStyle={{ background:BRAND.surface,border:`1px solid ${BRAND.border}`,borderRadius:10,fontSize:12 }}/>
                          <Area type="monotone" dataKey="% Gastos" fill="url(#gradRatio)" stroke="#EF4444" strokeWidth={2.5} dot={false} activeDot={{ r:5,fill:"#EF4444",stroke:"#fff",strokeWidth:2 }}/>
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>

                {/* ── Row 4: Categories % of income (horizontal) + installments ── */}
                <div style={{ display:"grid", gridTemplateColumns:"minmax(0,1fr) minmax(0,340px)", gap:16, marginBottom:16, alignItems:"start" }}>

                  {/* Category spend vs income */}
                  <div style={{ ...S.card, marginBottom:0 }}>
                    <div style={{ marginBottom:16 }}>
                      <div style={{ fontSize:11,fontWeight:700,color:BRAND.muted,letterSpacing:"0.06em",textTransform:"uppercase" }}>% POR CATEGORIA EM RELAÇÃO À RECEITA</div>
                      <div style={{ fontFamily:"'LyonDisplay',Georgia,serif",fontSize:18,fontWeight:400,color:BRAND.text,marginTop:4,letterSpacing:"-0.02em" }}>
                        Quanto cada categoria representa da renda
                      </div>
                    </div>
                    {catVsIncome.length === 0 ? (
                      <div style={{ color:BRAND.muted,fontSize:12,textAlign:"center",padding:"24px 0" }}>Sem dados</div>
                    ) : catVsIncome.map((c,i) => (
                      <div key={c.name} style={{ marginBottom:10 }}>
                        <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4 }}>
                          <div style={{ display:"flex",alignItems:"center",gap:6 }}>
                            <span style={{ fontSize:14 }}>{c.icon}</span>
                            <span style={{ fontSize:12,fontWeight:500,color:BRAND.text }}>{c.name}</span>
                          </div>
                          <div style={{ display:"flex",gap:10,alignItems:"center" }}>
                            <span style={{ fontSize:11,color:BRAND.muted }}>{fmt(c.total)}</span>
                            <span style={{ fontSize:12,fontWeight:700,color:c.color,minWidth:36,textAlign:"right" }}>{c.pctIncome.toFixed(1)}%</span>
                          </div>
                        </div>
                        <div style={{ height:6,background:BRAND.border,borderRadius:99,overflow:"hidden" }}>
                          <div style={{ height:"100%",width:`${Math.min(c.pctIncome,100)}%`,background:c.color,borderRadius:99,transition:"width 0.6s cubic-bezier(.4,0,.2,1)" }}/>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Installments projection */}
                  <div style={{ ...S.card, marginBottom:0 }}>
                    <div style={{ fontSize:11,fontWeight:700,color:BRAND.muted,letterSpacing:"0.06em",textTransform:"uppercase",marginBottom:4 }}>PARCELAS FUTURAS</div>
                    <div style={{ fontFamily:"'LyonDisplay',Georgia,serif",fontSize:18,fontWeight:400,color:BRAND.text,marginTop:4,marginBottom:16,letterSpacing:"-0.02em" }}>
                      Projeção dos próximos meses
                    </div>
                    {futureMonths.length === 0 ? (
                      <div style={{ color:BRAND.muted,fontSize:12,textAlign:"center",padding:"24px 0" }}>Nenhuma parcela futura detectada</div>
                    ) : (
                      <div style={{ display:"flex",flexDirection:"column",gap:10 }}>
                        {futureMonths.map(([key, data]) => {
                          const [y,m] = key.split("-");
                          const maxVal = Math.max(...futureMonths.map(([,d])=>d.total));
                          const pct = maxVal > 0 ? data.total/maxVal : 0;
                          return (
                            <div key={key}>
                              <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4 }}>
                                <span style={{ fontSize:12,fontWeight:600,color:BRAND.text }}>{MONTHS_FULL[+m-1]} {y}</span>
                                <span style={{ fontSize:13,fontWeight:700,color:BRAND.green }}>{fmt(data.total)}</span>
                              </div>
                              <div style={{ height:5,background:BRAND.border,borderRadius:99,overflow:"hidden" }}>
                                <div style={{ height:"100%",width:`${pct*100}%`,background:BRAND.green,borderRadius:99 }}/>
                              </div>
                              {data.items.slice(0,2).map((item,i)=>(
                                <div key={i} style={{ fontSize:10,color:BRAND.muted,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",marginTop:2 }}>
                                  <span style={{ color:BRAND.green,fontWeight:600 }}>{item.installment}</span> {item.desc}
                                </div>
                              ))}
                              {data.items.length>2 && <div style={{ fontSize:10,color:BRAND.muted,marginTop:1 }}>+{data.items.length-2} mais</div>}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                {/* ── Row 5: Balance evolution ── */}
                <div style={S.card}>
                  <div style={{ marginBottom:16 }}>
                    <div style={{ fontSize:11,fontWeight:700,color:BRAND.muted,letterSpacing:"0.06em",textTransform:"uppercase" }}>SALDO ACUMULADO</div>
                    <div style={{ fontFamily:"'LyonDisplay',Georgia,serif",fontSize:18,fontWeight:400,color:BRAND.text,marginTop:4,letterSpacing:"-0.02em" }}>
                      Evolução patrimonial ao longo do tempo
                    </div>
                  </div>
                  <div style={{ height:200 }}>
                    <ResponsiveContainer>
                      <ComposedChart data={balanceEvolution} margin={{ top:5,right:5,left:0,bottom:0 }}>
                        <defs>
                          <linearGradient id="gradAcum" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={BRAND.green} stopOpacity={0.22}/>
                            <stop offset="95%" stopColor={BRAND.green} stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={BRAND.border}/>
                        <XAxis dataKey="name" tick={{ fontSize:10,fill:BRAND.muted }} axisLine={false} tickLine={false}/>
                        <YAxis tick={{ fontSize:9,fill:BRAND.muted }} axisLine={false} tickLine={false} tickFormatter={v=>v>=1000?`${(v/1000).toFixed(0)}k`:v} width={38}/>
                        <ReferenceLine y={0} stroke={BRAND.border} strokeWidth={1.5}/>
                        <Tooltip content={<TT/>} cursor={{ stroke:BRAND.border, strokeWidth:1 }}/>
                        <Area type="monotone" dataKey="Acumulado" fill="url(#gradAcum)" stroke={BRAND.green} strokeWidth={2.5} dot={false} activeDot={{ r:5,fill:BRAND.green,stroke:"#fff",strokeWidth:2 }}/>
                        <Bar dataKey="Receita" fill="#22C55E" opacity={0.25} radius={[3,3,0,0]}/>
                        <Bar dataKey="Despesa" fill="#EF4444" opacity={0.25} radius={[3,3,0,0]}/>
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </>
            )}
          </div>
          );
        })()}

        {/* ═══════════════════════════════════
            FATURAS
            ═══════════════════════════════════ */}
        {page === "faturas" && (
          <FaturasPage
            invoices={invoices}
            setInvoices={setInvoices}
            transactions={realTransactions}
            setTransactions={setTransactions}
            accounts={accounts}
            categories={allCats}
            handleInvoiceImages={handleInvoiceImages}
            handleFiles={handleFiles}
            dragOverInvoice={dragOverInvoice}
            setDragOverInvoice={setDragOverInvoice}
            expandedInvoice={expandedInvoice}
            setExpandedInvoice={setExpandedInvoice}
            invoiceInputRef={invoiceInputRef}
            userId={authUser?.id}
          />
        )}

        {/* ═══════════════════════════════════
            PATRIMÔNIO
            ═══════════════════════════════════ */}
        {page === "patrimonio" && (
          <PatrimonioPage
            salary={salary}
            setSalary={(v) => { setSalary(v); supabase.from("user_profiles").upsert({ user_id: authUser.id, salary: v, updated_at: new Date().toISOString() }); }}
            accounts={accounts}
            setAccounts={(fn) => { setAccounts(prev => { const next = typeof fn === "function" ? fn(prev) : fn; const uid = authUser.id; const added = next.filter(a => !prev.find(x => x.id === a.id)); const removed = prev.filter(a => !next.find(x => x.id === a.id)); added.forEach(a => supabase.from("accounts").upsert({ ...a, user_id: uid })); removed.forEach(a => supabase.from("accounts").delete().eq("id", a.id).eq("user_id", uid)); return next; }); }}
            cards={cards}
            setCards={(fn) => { setCards(prev => { const next = typeof fn === "function" ? fn(prev) : fn; const uid = authUser.id; const added = next.filter(c => !prev.find(x => x.id === c.id)); const removed = prev.filter(c => !next.find(x => x.id === c.id)); added.forEach(c => supabase.from("cards").upsert({ ...c, user_id: uid })); removed.forEach(c => supabase.from("cards").delete().eq("id", c.id).eq("user_id", uid)); return next; }); }}
            assets={assets}
            setAssets={(fn) => { setAssets(prev => { const next = typeof fn === "function" ? fn(prev) : fn; const uid = authUser.id; const added = next.filter(a => !prev.find(x => x.id === a.id)); const removed = prev.filter(a => !next.find(x => x.id === a.id)); added.forEach(a => supabase.from("assets").upsert({ ...a, user_id: uid })); removed.forEach(a => supabase.from("assets").delete().eq("id", a.id).eq("user_id", uid)); return next; }); }}
            totalAccounts={totalAccounts}
            totalAssets={totalAssets}
            modalOpen={modalOpen} setModalOpen={setModalOpen}
            editItem={editItem} setEditItem={setEditItem}
          />
        )}

        {/* ═══════════════════════════════════
            ORÇAMENTO
            ═══════════════════════════════════ */}
        {page === "orcamento" && (
          <OrcamentoPage
            budgets={budgets}
            setBudgets={(newB) => { setBudgets(newB); const uid = authUser?.id; if (uid) { const rows = newB.map(b => ({ user_id: uid, cat_id: b.catId, fixed: b.fixed||0, variable: b.variable||0, planned: (b.fixed||0)+(b.variable||0) })); supabase.from("budgets").upsert(rows, { onConflict: "user_id,cat_id" }); } }}
            budgetMonth={budgetMonth} setBudgetMonth={setBudgetMonth}
            budgetComparison={budgetComparison}
            monthlyData={monthlyData}
            expCats={expCats}
            allCats={allCats}
            realTransactions={realTransactions}
          />
        )}

        {/* ═══════════════════════════════════
            CATEGORIAS
            ═══════════════════════════════════ */}
        {page === "categorias" && (
          <CategoriasPage
            categories={categories}
            setCategories={setCategories}
            modalOpen={modalOpen}
            setModalOpen={setModalOpen}
            editItem={editItem}
            setEditItem={setEditItem}
          />
        )}
      </div>

      {/* ═══ GLOBAL MODALS ═══ */}
      {modalOpen === "editAccount" && editItem && (
        <EditAccountModal item={editItem} onClose={() => { setModalOpen(null); setEditItem(null); }}
          onSave={(data) => {
            const updated = { ...editItem, ...data };
            setAccounts(p => p.map(a => a.id === editItem.id ? updated : a));
            supabase.from("accounts").upsert({ ...updated, user_id: authUser.id });
            setModalOpen(null); setEditItem(null);
          }}/>
      )}
      {modalOpen === "editCard" && editItem && (
        <EditCardModal item={editItem} onClose={() => { setModalOpen(null); setEditItem(null); }}
          onSave={(data) => {
            const updated = { ...editItem, ...data };
            setCards(p => p.map(c => c.id === editItem.id ? updated : c));
            supabase.from("cards").upsert({ ...updated, user_id: authUser.id });
            setModalOpen(null); setEditItem(null);
          }}/>
      )}

      {/* Confirm clear */}
      {confirmLogout && (
        <Modal open={true} onClose={() => setConfirmLogout(false)} title="Sair da conta?" noClickOutside>
          <p style={{ fontSize:14,color:"#374151",marginBottom:20,lineHeight:1.6 }}>
            Tem certeza que deseja sair? Seus dados ficam salvos e você pode entrar novamente a qualquer momento.
          </p>
          <div style={{ display:"flex",gap:10 }}>
            <button style={{ ...S.btn2,flex:1,justifyContent:"center" }} onClick={() => setConfirmLogout(false)}>Cancelar</button>
            <button style={{ ...S.btn1,flex:1,justifyContent:"center" }} onClick={() => { supabase.auth.signOut(); setAuthUser(null); setTransactions([]); setInvoices([]); setAccounts([]); setCards([]); setAssets([]); setSalary(0); setConfirmLogout(false); }}><LogOut size={14}/> Sair</button>
          </div>
        </Modal>
      )}

      {confirmClear && (
        <Modal open={true} onClose={() => setConfirmClear(false)} title="⚠️ Limpar todos os dados?" noClickOutside>
          <p style={{ fontSize:14,color:"#374151",marginBottom:20,lineHeight:1.6 }}>
            Isso irá remover <strong>todas as faturas, transações, contas, cartões e investimentos</strong>. Esta ação não pode ser desfeita.
          </p>
          <div style={{ display:"flex",gap:10 }}>
            <button style={{ ...S.btn2,flex:1,justifyContent:"center" }} onClick={() => setConfirmClear(false)}>Cancelar</button>
            <button style={{ ...S.btn1,flex:1,justifyContent:"center",background:"#EF4444" }} onClick={clearAllData}><Trash2 size={14}/> Limpar tudo</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   PAGE: REVIEW (import confirmation)
   ══════════════════════════════════════════════════════════════ */
function ReviewPage({ draft, setDraft, categories, accounts, onConfirm, onCancel, onLearnRule }) {
  const now = new Date();
  const [items, setItems] = useState(draft.items || []);
  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState("all");
  const [filterReview, setFilterReview] = useState("all"); // "all"|"uncategorized"
  const [selectedMonth, setSelectedMonth] = useState(draft.importMonth || `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}`);
  const [selectedBank, setSelectedBank] = useState("");
  const [invoiceName, setInvoiceName] = useState(draft.fileName || "");
  const [showImage, setShowImage] = useState(false);

  // Keep items in sync when draft.items updates (async extraction)
  useEffect(() => {
    setItems(draft.items || []);
  }, [draft.items]);

  const fmtLocal = v => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  const expCats = categories.filter(c => c.type === "expense");
  const allCats = categories;

  const uncategorizedCount = items.filter(t => t.needsReview).length;
  const totalValue = items.filter(t => t.type === "expense").reduce((s, t) => s + t.value, 0);

  const updateItemCat = (id, catId) => {
    const catObj = allCats.find(c => c.id === catId);
    setItems(p => p.map(t => {
      if (t.id !== id) return t;
      // Learn this correction for future imports
      if (onLearnRule && t.description) onLearnRule(t.description, catId);
      return { ...t, category: catId, needsReview: false, type: catObj?.type === "income" ? "income" : "expense" };
    }));
  };

  const removeItem = (id) => setItems(p => p.filter(t => t.id !== id));

  const filtered = useMemo(() => {
    return items.filter(t => {
      if (filterReview === "uncategorized" && !t.needsReview) return false;
      if (filterCat !== "all" && t.category !== filterCat) return false;
      if (search) {
        const s = search.toLowerCase();
        if (!t.description.toLowerCase().includes(s)) return false;
      }
      return true;
    });
  }, [items, search, filterCat, filterReview]);

  const handleConfirm = () => {
    const [year, month] = selectedMonth.split("-").map(Number);
    // Force all transactions to use the selected month/year, keeping only the day
    const finalItems = items.map(t => {
      const day = t.date ? new Date(t.date).getDate() : 1;
      return { ...t, date: new Date(year, month - 1, day) };
    });
    onConfirm(finalItems, month - 1, year, selectedBank || null, invoiceName);
  };

  return (
    <div style={{ maxWidth: 900, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20,flexWrap:"wrap",gap:12 }}>
        <div>
          <div style={{ fontSize:11,fontWeight:600,color:BRAND.muted,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:4 }}>
            Radar de Gastos
          </div>
          <div style={S.pageTitle}>Revisão de categorias</div>
          <div style={S.pageDesc}>{draft.fileName}</div>
        </div>
        <div style={{ display:"flex",gap:10 }}>
          <button style={S.btn2} onClick={onCancel}><ChevronLeft size={14}/> Voltar</button>
          <button style={{ ...S.btn1, background:"#22C55E" }} onClick={handleConfirm} disabled={draft.extracting}>
            <Check size={14}/> Confirmar e Analisar
          </button>
        </div>
      </div>

      {/* Loading state */}
      {draft.extracting && (
        <div style={{ ...S.card, borderLeft:`4px solid ${BRAND.green}`, display:"flex",alignItems:"center",gap:12 }}>
          <RefreshCw size={18} color={BRAND.green} style={{ animation:"spin 1s linear infinite",flexShrink:0 }}/>
          <div>
            <div style={{ fontSize:13,fontWeight:600,color:BRAND.green }}>Extraindo lançamentos via IA...</div>
            <div style={{ fontSize:12,color:"#9CA3AF" }}>Aguarde enquanto analisamos a imagem da fatura</div>
          </div>
          <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
        </div>
      )}

      {draft.extractError && (
        <div style={{ ...S.card, borderLeft:"4px solid #F59E0B", color:"#92400E", fontSize:13 }}>
          ⚠️ {draft.extractError}
        </div>
      )}

      {/* Config row: month + bank + image preview */}
      <div style={{ ...S.card, marginBottom:12 }}>
        <div style={{ display:"flex",gap:14,flexWrap:"wrap",alignItems:"flex-end" }}>
          <div style={{ flex:1,minWidth:160 }}>
            <label style={{ fontSize:11,fontWeight:600,color:"#6B7280",display:"block",marginBottom:4 }}>NOME / DESCRIÇÃO</label>
            <input value={invoiceName} onChange={e => setInvoiceName(e.target.value)} style={{ ...S.input }} placeholder="Ex: Nubank Maio"/>
          </div>
          <div style={{ minWidth:160 }}>
            <label style={{ fontSize:11,fontWeight:600,color:"#6B7280",display:"block",marginBottom:4 }}>MÊS DE REFERÊNCIA</label>
            <input type="month" value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)} style={{ ...S.sel, width:"100%" }}/>
          </div>
          <div style={{ minWidth:160 }}>
            <label style={{ fontSize:11,fontWeight:600,color:"#6B7280",display:"block",marginBottom:4 }}>BANCO / CARTÃO</label>
            <select value={selectedBank} onChange={e => setSelectedBank(e.target.value)} style={{ ...S.sel, width:"100%" }}>
              <option value="">Sem vínculo</option>
              {accounts.map(a => <option key={a.id} value={a.id}>{a.icon||"🏦"} {a.name}</option>)}
            </select>
          </div>
          {draft.imageUrl && (
            <button style={{ ...S.btn2, alignSelf:"flex-end" }} onClick={() => setShowImage(true)}>
              <Eye size={13}/> Ver imagem
            </button>
          )}
        </div>
      </div>

      {/* Uncategorized alert */}
      {uncategorizedCount > 0 && (
        <div style={{ ...S.card, borderLeft:"4px solid #F59E0B", background:"#FFFBEB", marginBottom:12 }}>
          <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8 }}>
            <div style={{ display:"flex",alignItems:"center",gap:8 }}>
              <AlertCircle size={16} color="#F59E0B"/>
              <span style={{ fontSize:13,fontWeight:600,color:"#92400E" }}>{uncategorizedCount} transações sem categoria definida</span>
            </div>
            <button
              style={{ ...S.btnSm, background:"#FEF3C7", color:"#92400E", border:"1px solid #FDE68A" }}
              onClick={() => setFilterReview(filterReview === "uncategorized" ? "all" : "uncategorized")}
            >
              {filterReview === "uncategorized" ? "Ver todas" : "Filtrar sem categoria"}
            </button>
          </div>
        </div>
      )}

      {/* Search + filter bar */}
      <div style={{ ...S.card, marginBottom:12 }}>
        <div style={{ display:"flex",gap:10,flexWrap:"wrap",alignItems:"center" }}>
          <div style={{ flex:1,minWidth:180,position:"relative" }}>
            <Search size={14} color="#9CA3AF" style={{ position:"absolute",left:10,top:"50%",transform:"translateY(-50%)" }}/>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Buscar..."
              style={{ ...S.input, paddingLeft:32 }}
            />
          </div>
          <select value={filterCat} onChange={e => setFilterCat(e.target.value)} style={{ ...S.sel, minWidth:140 }}>
            <option value="all">Todos</option>
            {allCats.map(c => <option key={c.id} value={c.id}>{c.icon} {c.label}</option>)}
          </select>
          <div style={{ fontSize:12,color:"#9CA3AF",whiteSpace:"nowrap" }}>
            {filtered.length} de {items.length} · Total: <strong style={{ color:"#111" }}>{fmtLocal(totalValue)}</strong>
          </div>
        </div>
      </div>

      {/* Transactions list */}
      {draft.extracting && items.length === 0 ? (
        <div style={{ ...S.card, textAlign:"center", padding:40, color:"#9CA3AF" }}>
          Aguardando extração...
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ ...S.card, textAlign:"center", padding:40, color:"#9CA3AF" }}>
          Nenhuma transação encontrada
        </div>
      ) : (
        <>
          {/* Uncategorized section */}
          {filterReview !== "all" || filtered.some(t => t.needsReview) ? (
            <div style={{ ...S.card, borderLeft:"4px solid #F59E0B", marginBottom:12 }}>
              <div style={{ fontSize:13,fontWeight:700,color:"#92400E",marginBottom:10 }}>
                <AlertCircle size={14} style={{ verticalAlign:"middle",marginRight:4 }}/> Sem categoria — escolha abaixo
              </div>
              {filtered.filter(t => t.needsReview).map(t => (
                <ReviewRow key={t.id} t={t} allCats={allCats} onCatChange={updateItemCat} onRemove={removeItem} fmtLocal={fmtLocal} highlight/>
              ))}
            </div>
          ) : null}

          {/* All transactions table */}
          <div style={S.card}>
            <div style={{ fontSize:13,fontWeight:700,color:"#374151",marginBottom:10 }}>
              <Tag size={14} style={{ verticalAlign:"middle",marginRight:4 }}/> Todas as transações ({filtered.length})
            </div>
            <div style={{ overflowX:"auto" }}>
              <table style={{ width:"100%",borderCollapse:"collapse" }}>
                <thead>
                  <tr>
                    <th style={{ ...S.th, width:90 }}>DATA</th>
                    <th style={S.th}>DESCRIÇÃO</th>
                    <th style={{ ...S.th, width:100 }}>VALOR</th>
                    <th style={{ ...S.th, width:200 }}>CATEGORIA</th>
                    <th style={{ ...S.th, width:40 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(t => (
                    <ReviewRow key={t.id} t={t} allCats={allCats} onCatChange={updateItemCat} onRemove={removeItem} fmtLocal={fmtLocal} tableMode/>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Bottom confirm bar */}
      <div style={{ position:"sticky",bottom:0,background:"#F8F9FB",borderTop:"1px solid #F0F0F5",padding:"14px 0",display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:10,marginTop:8 }}>
        <div style={{ fontSize:13,color:"#6B7280" }}>
          <strong style={{ color:"#111" }}>{items.length}</strong> lançamentos · <strong style={{ color:"#EF4444" }}>{fmtLocal(totalValue)}</strong> em despesas
          {uncategorizedCount > 0 && <span style={{ color:"#F59E0B",marginLeft:8 }}>· {uncategorizedCount} sem categoria</span>}
        </div>
        <div style={{ display:"flex",gap:10 }}>
          <button style={S.btn2} onClick={onCancel}><ChevronLeft size={14}/> Cancelar</button>
          <button style={{ ...S.btn1, background:"#22C55E" }} onClick={handleConfirm} disabled={draft.extracting}>
            <Check size={14}/> Confirmar e Analisar
          </button>
        </div>
      </div>

      {/* Image fullscreen */}
      {showImage && draft.imageUrl && (
        <div style={S.modal} onClick={() => setShowImage(false)}>
          <div style={{ position:"relative",maxWidth:"95vw",maxHeight:"90vh" }} onClick={e => e.stopPropagation()}>
            <img src={draft.imageUrl} alt="Fatura" style={{ maxWidth:"100%",maxHeight:"90vh",borderRadius:12,boxShadow:"0 24px 48px rgba(0,0,0,0.3)" }}/>
            <button onClick={() => setShowImage(false)} style={{ position:"absolute",top:-12,right:-12,...S.btn1,background:"#fff",color:"#374151",border:"1px solid #E5E7EB",padding:6,borderRadius:"50%" }}>
              <X size={16}/>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ReviewRow({ t, allCats, onCatChange, onRemove, fmtLocal, tableMode, highlight }) {
  const cat = allCats.find(c => c.id === t.category);
  const rowBg = highlight ? "#FFFBEB" : t.needsReview ? "#FFFBEB" : "transparent";

  if (tableMode) {
    return (
      <tr style={{ background: t.needsReview ? "#FFFBEB" : "transparent" }}>
        <td style={{ ...S.td, fontSize:12, color:"#9CA3AF", whiteSpace:"nowrap" }}>
          {t.date ? t.date.toLocaleDateString("pt-BR") : "—"}
        </td>
        <td style={{ ...S.td, fontWeight:500, maxWidth:260 }}>
          <div style={{ display:"flex", alignItems:"center", gap:6, overflow:"hidden" }}>
            {t.needsReview && <span style={{ color:"#F59E0B", flexShrink:0 }}>?</span>}
            <span style={{ overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{t.description}</span>
            {t.installment && t.installment.total > 1 && (
              <span style={{ flexShrink:0, fontSize:10, fontWeight:700, background:"#EAF5EF", color:BRAND.green, borderRadius:4, padding:"1px 5px", whiteSpace:"nowrap" }}>
                {t.installment.current}/{t.installment.total}x
              </span>
            )}
          </div>
        </td>
        <td style={{ ...S.td, fontWeight:700, color:"#111827", whiteSpace:"nowrap" }}>
          {fmtLocal(t.value)}
          {t.installment && t.installment.total > 1 && (
            <div style={{ fontSize:10, color:"#9CA3AF", fontWeight:400 }}>parcela de {fmtLocal(t.value)}</div>
          )}
        </td>
        <td style={S.td}>
          <select
            value={t.category}
            onChange={e => onCatChange(t.id, e.target.value)}
            style={{
              ...S.sel, fontSize:12, padding:"4px 8px",
              borderColor: (cat?.color || "#ccc") + "60",
              background: (cat?.color || "#ccc") + "10",
            }}
          >
            {allCats.map(c => <option key={c.id} value={c.id}>{c.icon} {c.label}</option>)}
          </select>
        </td>
        <td style={S.td}>
          <button onClick={() => onRemove(t.id)} style={{ ...S.btnSm, background:"#FEE2E2", color:"#DC2626", padding:"3px 6px" }}>
            <X size={10}/>
          </button>
        </td>
      </tr>
    );
  }

  // Card mode (for uncategorized section)
  return (
    <div style={{ display:"flex",alignItems:"center",gap:12,padding:"10px 12px",background:"#FFFBEB",borderRadius:10,border:"1px solid #FEF3C7",marginBottom:6 }}>
      <div style={{ flex:1,minWidth:0 }}>
        <div style={{ fontSize:13,fontWeight:500,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>{t.description}</div>
        <div style={{ fontSize:11,color:"#9CA3AF" }}>{t.date ? t.date.toLocaleDateString("pt-BR") : "—"} · {fmtLocal(t.value)}</div>
      </div>
      <select
        value={t.category}
        onChange={e => onCatChange(t.id, e.target.value)}
        style={{ ...S.sel, fontSize:12, minWidth:140,
          borderColor: (cat?.color || "#ccc") + "60",
          background: (cat?.color || "#ccc") + "10",
        }}
      >
        {allCats.map(c => <option key={c.id} value={c.id}>{c.icon} {c.label}</option>)}
      </select>
      <button onClick={() => onRemove(t.id)} style={{ ...S.btnSm, background:"#FEE2E2", color:"#DC2626", padding:"3px 6px" }}>
        <X size={10}/>
      </button>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   PAGE: FATURAS
   ══════════════════════════════════════════════════════════════ */
function FaturasPage({ invoices, setInvoices, transactions, setTransactions, accounts, categories, handleInvoiceImages, handleFiles, dragOverInvoice, setDragOverInvoice, expandedInvoice, setExpandedInvoice, invoiceInputRef, userId }) {
  const now = new Date();
  const [viewImage, setViewImage] = useState(null);
  const [importMonth, setImportMonth] = useState(`${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}`);

  const fmtLocal = v => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  const expCats = categories.filter(c => c.type === "expense");

  const structuredByMonth = useMemo(() => {
    const groups = {};
    transactions.forEach(t => {
      if (!t.date) return;
      const key = `${t.date.getFullYear()}-${String(t.date.getMonth()+1).padStart(2,"0")}`;
      if (!groups[key]) groups[key] = { income: 0, expense: 0, count: 0, imports: {}, catBreakdown: {} };
      groups[key][t.type === "income" ? "income" : "expense"] += t.value;
      groups[key].count++;
      // Track by importId for deletion
      if (t.importId) {
        if (!groups[key].imports[t.importId]) groups[key].imports[t.importId] = { name: t.importName || t.source || t.importId, count: 0, total: 0 };
        groups[key].imports[t.importId].count++;
        if (t.type !== "income") groups[key].imports[t.importId].total += t.value;
      }
      // Category breakdown (expenses only)
      if (t.type !== "income" && t.category) {
        if (!groups[key].catBreakdown[t.category]) groups[key].catBreakdown[t.category] = { total: 0 };
        groups[key].catBreakdown[t.category].total += t.value;
      }
    });
    return groups;
  }, [transactions]);

  const allMonthKeys = useMemo(() => {
    const keys = new Set();
    invoices.forEach(inv => keys.add(`${inv.year}-${String(inv.month+1).padStart(2,"0")}`));
    Object.keys(structuredByMonth).forEach(k => keys.add(k));
    return Array.from(keys).sort().reverse();
  }, [invoices, structuredByMonth]);

  const handleDropAny = (e) => {
    e.preventDefault();
    setDragOverInvoice(false);
    const files = Array.from(e.dataTransfer.files);
    const structured = files.filter(f => ["csv","xls","xlsx"].includes(f.name.split(".").pop().toLowerCase()));
    if (structured.length > 0) handleFiles(structured, importMonth);
  };

  const handleAnyInput = (e) => {
    const files = Array.from(e.target.files);
    const structured = files.filter(f => ["csv","xls","xlsx"].includes(f.name.split(".").pop().toLowerCase()));
    if (structured.length > 0) handleFiles(structured, importMonth);
    e.target.value = "";
  };

  const deleteInvoice = (id) => { setInvoices(p => p.filter(inv => inv.id !== id)); if (userId) supabase.from("invoices").delete().eq("id", id).eq("user_id", userId); };

  return (
    <div>
      <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16,flexWrap:"wrap",gap:10 }}>
        <div>
          <div style={S.pageTitle}>Faturas</div>
          <div style={S.pageDesc}>Importe e revise faturas antes de confirmar — CSV, XLS</div>
        </div>
        <button style={S.btn1} onClick={() => invoiceInputRef.current?.click()}>
          <Upload size={14}/> Importar Fatura
        </button>
      </div>

      <input ref={invoiceInputRef} type="file" accept=".csv,.xls,.xlsx" multiple hidden onChange={handleAnyInput}/>

      {/* Month selector for import */}
      <div style={{ ...S.card, padding:"12px 16px", marginBottom:12, display:"flex", alignItems:"center", gap:12, flexWrap:"wrap" }}>
        <span style={{ fontSize:13,fontWeight:600,color:"#374151" }}>📅 Mês de referência da importação:</span>
        <input type="month" value={importMonth} onChange={e => setImportMonth(e.target.value)} style={S.sel}/>
        <span style={{ fontSize:12,color:"#9CA3AF" }}>Este mês será pré-selecionado na revisão</span>
      </div>

      {/* Drop zone */}
      <div style={S.card}>
        <div style={S.dz(dragOverInvoice)}
          onDragOver={e => { e.preventDefault(); setDragOverInvoice(true); }}
          onDragLeave={() => setDragOverInvoice(false)}
          onDrop={handleDropAny}
          onClick={() => invoiceInputRef.current?.click()}>
          <Upload size={34} color={dragOverInvoice?BRAND.green:"#9CA3AF"} strokeWidth={1.5} style={{ marginBottom:10 }}/>
          <div style={{ fontSize:14,fontWeight:600,color:"#374151" }}>Arraste arquivos aqui</div>
          <div style={{ fontSize:12,color:"#9CA3AF",marginTop:4 }}>Você vai revisar e confirmar antes de importar</div>
          <div style={{ display:"flex",justifyContent:"center",gap:5,marginTop:10,flexWrap:"wrap" }}>
            {["CSV","XLS","XLSX"].map(x => <span key={x} style={S.badge("#6B7280")}>{x}</span>)}
          </div>
        </div>
      </div>

      {/* Month groups */}
      {allMonthKeys.length === 0 ? (
        <div style={{ ...S.card, textAlign:"center", padding:40, color:"#9CA3AF", fontSize:13 }}>
          Nenhuma fatura importada ainda
        </div>
      ) : allMonthKeys.map(monthKey => {
        const [y, m] = monthKey.split("-");
        const label = `${MONTHS_FULL[+m-1]} ${y}`;
        const invList = invoices.filter(inv => `${inv.year}-${String(inv.month+1).padStart(2,"0")}` === monthKey);
        const structData = structuredByMonth[monthKey] || { income:0, expense:0, count:0 };
        const invTotal = invList.reduce((s, inv) => s + (inv.manualItems?.reduce((a,b) => a+b.value, 0)||0), 0);
        const totalMonth = invTotal + structData.expense;
        const isExpanded = expandedInvoice === monthKey;

        return (
          <div key={monthKey} style={{ ...S.card, marginBottom:10 }}>
            <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",cursor:"pointer" }}
              onClick={() => setExpandedInvoice(isExpanded ? null : monthKey)}>
              <div style={{ display:"flex",alignItems:"center",gap:12 }}>
                <div style={{ width:42,height:42,borderRadius:12,background:"#EAF5EF",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>
                  <Calendar size={20} color={BRAND.green}/>
                </div>
                <div>
                  <div style={{ fontSize:15,fontWeight:700,color:"#111" }}>{label}</div>
                  <div style={{ fontSize:12,color:"#9CA3AF" }}>
                    Gastos: {fmtLocal(totalMonth)}
                    {structData.income > 0 && ` · Receitas: ${fmtLocal(structData.income)}`}
                  </div>
                </div>
              </div>
              <div style={{ display:"flex",alignItems:"center",gap:8 }}>
                <span style={{ ...S.badge("#EF4444") }}>{fmtLocal(totalMonth)}</span>
                {isExpanded ? <ChevronUp size={18} color="#9CA3AF"/> : <ChevronDown size={18} color="#9CA3AF"/>}
              </div>
            </div>

            {isExpanded && (
              <div style={{ marginTop:14,paddingTop:14,borderTop:"1px solid #F3F4F6" }}>
                {structData.count > 0 && (() => {
                  // Category breakdown sorted by total desc
                  const catRows = Object.entries(structData.catBreakdown || {})
                    .map(([catId, d]) => {
                      const cat = categories.find(c => c.id === catId);
                      return { catId, total: d.total, label: cat?.label || catId, icon: cat?.icon || "•", color: cat?.color || "#6B7280" };
                    })
                    .sort((a, b) => b.total - a.total);
                  const totalExp = structData.expense || 1;
                  const importEntries = Object.entries(structData.imports || {});
                  return (
                    <div style={{ background:"#F0FDF4",borderRadius:10,padding:14,marginBottom:10,border:"1px solid #BBF7D0" }}>
                      {/* Import groups with delete buttons */}
                      {importEntries.map(([importId, imp]) => (
                        <div key={importId} style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8 }}>
                          <div>
                            <div style={{ fontSize:13,fontWeight:600,color:"#166534" }}>📊 {imp.name} — {imp.count} lançamentos</div>
                            <div style={{ fontSize:11,color:"#15803D" }}>Despesas: {fmtLocal(imp.total)}</div>
                          </div>
                          <button style={{ ...S.btnDanger, fontSize:11, padding:"4px 8px" }}
                            onClick={() => { setTransactions(p => p.filter(t => t.importId !== importId)); if (userId) supabase.from("transactions").delete().eq("import_id", importId).eq("user_id", userId); }}>
                            <Trash2 size={12}/> Excluir
                          </button>
                        </div>
                      ))}
                      {importEntries.length === 0 && (
                        <div style={{ fontSize:13,fontWeight:600,color:"#166534",marginBottom:8 }}>📊 Extrato — {structData.count} lançamentos</div>
                      )}
                      {/* Category bars */}
                      {catRows.length > 0 && (
                        <div style={{ marginTop:10,paddingTop:10,borderTop:"1px solid #BBF7D0" }}>
                          {catRows.map(r => {
                            const pct = r.total / totalExp;
                            return (
                              <div key={r.catId} style={{ marginBottom:8 }}>
                                <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:3 }}>
                                  <span style={{ fontSize:12,fontWeight:500,color:"#374151" }}>{r.icon} {r.label}</span>
                                  <span style={{ fontSize:12,fontWeight:600,color:"#111" }}>{fmtLocal(r.total)} <span style={{ color:"#9CA3AF",fontWeight:400 }}>({(pct*100).toFixed(0)}%)</span></span>
                                </div>
                                <div style={{ height:6,borderRadius:99,background:"#D1FAE5",overflow:"hidden" }}>
                                  <div style={{ height:"100%",width:`${pct*100}%`,background:r.color,borderRadius:99,transition:"width 0.4s" }}/>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })()}
                {invList.map(inv => (
                  <InvoiceCard key={inv.id} inv={inv} setInvoices={(fn) => { setInvoices(prev => { const next = typeof fn === "function" ? fn(prev) : fn; const updated = next.find(i => i.id === inv.id); if (updated && userId) supabase.from("invoices").update({ manual_items: updated.manualItems, total: updated.total }).eq("id", inv.id).eq("user_id", userId); return next; }); }} accounts={accounts}
                    categories={expCats} onDelete={() => deleteInvoice(inv.id)} onViewImage={() => setViewImage(inv.imageUrl)}/>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {viewImage && (
        <div style={S.modal} onClick={() => setViewImage(null)}>
          <div style={{ position:"relative",maxWidth:"95vw",maxHeight:"90vh" }} onClick={e => e.stopPropagation()}>
            <img src={viewImage} alt="Fatura" style={{ maxWidth:"100%",maxHeight:"90vh",borderRadius:12,boxShadow:"0 24px 48px rgba(0,0,0,0.3)" }}/>
            <button onClick={() => setViewImage(null)} style={{ position:"absolute",top:-12,right:-12,...S.btn1,background:"#fff",color:"#374151",border:"1px solid #E5E7EB",padding:6,borderRadius:"50%" }}><X size={16}/></button>
          </div>
        </div>
      )}
    </div>
  );
}

function InvoiceCard({ inv, setInvoices, accounts, categories, onDelete, onViewImage }) {
  const [addItem, setAddItem] = useState(false);
  const [itemDesc, setItemDesc] = useState("");
  const [itemValue, setItemValue] = useState("");
  const [itemCat, setItemCat] = useState(categories[0]?.id || "outros_desp");

  const fmtLocal = v => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  const items = inv.manualItems || [];
  const total = items.reduce((s, i) => s + i.value, 0);

  const updateItemCat = (itemId, catId) => {
    const cat = categories.find(c => c.id === catId);
    setInvoices(p => p.map(inv2 => {
      if (inv2.id !== inv.id) return inv2;
      return { ...inv2, manualItems: (inv2.manualItems||[]).map(x =>
        x.id === itemId ? { ...x, category: catId, catLabel: cat?.label||"", catIcon: cat?.icon||"", catColor: cat?.color||"#ccc" } : x
      )};
    }));
  };

  const addManualItem = () => {
    const val = parseFloat(itemValue.replace(",",".")) || 0;
    if (!itemDesc.trim() || val === 0) return;
    const cat = categories.find(c => c.id === itemCat);
    const newItem = {
      id: `item-${Date.now()}`,
      description: itemDesc.trim(),
      value: val,
      category: itemCat,
      catLabel: cat?.label || "",
      catIcon: cat?.icon || "",
      catColor: cat?.color || "#ccc",
    };
    setInvoices(p => p.map(i => i.id === inv.id ? { ...i, manualItems: [...(i.manualItems||[]), newItem], total: total + val } : i));
    setItemDesc(""); setItemValue(""); setAddItem(false);
  };

  const removeItem = (itemId) => {
    setInvoices(p => p.map(i => {
      if (i.id !== inv.id) return i;
      const updated = (i.manualItems||[]).filter(x => x.id !== itemId);
      return { ...i, manualItems: updated, total: updated.reduce((s,x) => s+x.value,0) };
    }));
  };

  const bankName = accounts.find(a => a.id === inv.bankId)?.name;

  return (
    <div style={{ background:"#F9FAFB",borderRadius:12,padding:14,marginBottom:10,border:"1px solid #F0F0F5" }}>
      {/* Header */}
      <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10 }}>
        <div>
          <div style={{ fontSize:13,fontWeight:700,color:"#111" }}>{inv.name}</div>
          {bankName && <div style={{ fontSize:11,color:"#9CA3AF",marginTop:2 }}>🏦 {bankName}</div>}
        </div>
        <div style={{ display:"flex",gap:6 }}>
          {inv.imageUrl && (
            <button style={{ ...S.btnSm,background:"#EAF5EF",color:BRAND.green }} onClick={onViewImage}><Eye size={12}/> Ver imagem</button>
          )}
          <button style={S.btnDanger} onClick={onDelete}><Trash2 size={12}/></button>
        </div>
      </div>

      {/* Extracting state */}
      {inv.extracting && (
        <div style={{ display:"flex",alignItems:"center",gap:8,padding:12,background:"#EAF5EF",borderRadius:8,marginBottom:10,fontSize:12,color:BRAND.green,fontWeight:600 }}>
          <RefreshCw size={14} style={{ animation:"spin 1s linear infinite" }}/> Extraindo lançamentos via IA...
        </div>
      )}
      {inv.extractError && (
        <div style={{ padding:10,background:"#FEF3C7",borderRadius:8,marginBottom:10,fontSize:12,color:"#92400E" }}>
          ⚠️ {inv.extractError}
        </div>
      )}

      {/* Items list */}
      {items.length > 0 && (
        <div style={{ marginBottom:10 }}>
          {items.map(item => (
            <div key={item.id} style={{ display:"flex",alignItems:"center",gap:8,padding:"7px 0",borderBottom:"1px solid #F0F0F5" }}>
              <span style={{ fontSize:14,flexShrink:0 }}>{item.catIcon}</span>
              <div style={{ flex:1,minWidth:0 }}>
                <div style={{ fontSize:12,fontWeight:500,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>{item.description}</div>
                {item.dateStr && <div style={{ fontSize:10,color:"#9CA3AF" }}>{item.dateStr}</div>}
              </div>
              <select
                value={item.category}
                onChange={e => updateItemCat(item.id, e.target.value)}
                style={{ ...S.sel, fontSize:11, padding:"3px 8px", maxWidth:130 }}
              >
                {categories.map(c => <option key={c.id} value={c.id}>{c.icon} {c.label}</option>)}
              </select>
              <div style={{ fontSize:13,fontWeight:700,whiteSpace:"nowrap" }}>{fmtLocal(item.value)}</div>
              <button onClick={() => removeItem(item.id)} style={{ ...S.btnSm,background:"#FEE2E2",color:"#DC2626",padding:"3px 6px",flexShrink:0 }}><X size={10}/></button>
            </div>
          ))}
          <div style={{ display:"flex",justifyContent:"flex-end",marginTop:8,fontWeight:700,fontSize:13,color:"#111" }}>
            Total: {fmtLocal(total)}
          </div>
        </div>
      )}

      {/* Add item form */}
      {!inv.extracting && (addItem ? (
        <div style={{ background:"#fff",borderRadius:10,padding:12,border:"1px solid #E5E7EB" }}>
          <div style={{ display:"grid",gridTemplateColumns:"1fr 100px",gap:8,marginBottom:8 }}>
            <input value={itemDesc} onChange={e => setItemDesc(e.target.value)} placeholder="Descrição" style={S.input}/>
            <input value={itemValue} onChange={e => setItemValue(e.target.value)} placeholder="R$" style={S.input} type="number"/>
          </div>
          <div style={{ marginBottom:10 }}>
            <select value={itemCat} onChange={e => setItemCat(e.target.value)} style={{ ...S.sel,width:"100%" }}>
              {categories.map(c => <option key={c.id} value={c.id}>{c.icon} {c.label}</option>)}
            </select>
          </div>
          <div style={{ display:"flex",gap:8 }}>
            <button style={{ ...S.btn2,flex:1,justifyContent:"center",fontSize:12 }} onClick={() => setAddItem(false)}>Cancelar</button>
            <button style={{ ...S.btn1,flex:1,justifyContent:"center",fontSize:12 }} onClick={addManualItem}><Check size={12}/> Adicionar</button>
          </div>
        </div>
      ) : (
        <button style={{ ...S.btn2,width:"100%",justifyContent:"center",fontSize:12 }} onClick={() => setAddItem(true)}>
          <Plus size={12}/> Adicionar item manualmente
        </button>
      ))}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   PAGE: PATRIMÔNIO
   ══════════════════════════════════════════════════════════════ */
function PatrimonioPage({ salary, setSalary, accounts, setAccounts, cards, setCards, assets, setAssets, totalAccounts, totalAssets, modalOpen, setModalOpen, editItem, setEditItem }) {
  const [newAssetName, setNewAssetName] = useState("");
  const [newAssetType, setNewAssetType] = useState("Renda Fixa");
  const [newAssetValue, setNewAssetValue] = useState("");
  const [newAssetRate, setNewAssetRate] = useState("");
  const [showAssetForm, setShowAssetForm] = useState(false);

  const fmt = v => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  const totalPatrimonio = totalAccounts + totalAssets;

  const ASSET_TYPES = ["Renda Fixa","Ações","FII","ETF","Criptomoeda","Previdência","Outro"];
  const ASSET_COLORS = { "Renda Fixa":"#22C55E","Ações":"#3B82F6","FII":"#EC4899","ETF":"#8B5CF6","Criptomoeda":"#F59E0B","Previdência":"#06B6D4","Outro":"#94A3B8" };

  const addAsset = () => {
    const val = parseFloat(newAssetValue.replace(",",".")) || 0;
    if (!newAssetName.trim() || val === 0) return;
    setAssets(p => [...p, {
      id: `a${Date.now()}`,
      name: newAssetName,
      type: newAssetType,
      value: val,
      rate: newAssetRate || "—",
      color: ASSET_COLORS[newAssetType] || "#94A3B8",
    }]);
    setNewAssetName(""); setNewAssetValue(""); setNewAssetRate(""); setShowAssetForm(false);
  };

  // Pie data for assets
  const assetsByType = useMemo(() => {
    const byType = {};
    assets.forEach(a => { byType[a.type] = (byType[a.type]||0) + a.value; });
    return Object.entries(byType).map(([name, value]) => ({
      name, value, color: ASSET_COLORS[name] || "#94A3B8",
      pct: totalAssets > 0 ? value / totalAssets : 0,
    }));
  }, [assets, totalAssets]);

  return (
    <div>
      <div style={S.pageTitle}>Patrimônio</div>
      <div style={S.pageDesc}>Salário, contas, cartões e investimentos</div>

      {/* KPIs */}
      <div style={S.metricRow}>
        {[
          { l:"Salário", v:fmt(salary), bg:"#DCFCE7", c:"#22C55E", ic:<DollarSign size={16}/> },
          { l:"Saldo em Contas", v:fmt(totalAccounts), bg:"#EAF5EF", c:BRAND.green, ic:<Wallet size={16}/> },
          { l:"Investido", v:fmt(totalAssets), bg:"#F3E8FF", c:"#8B5CF6", ic:<Briefcase size={16}/> },
          { l:"Patrimônio Total", v:fmt(totalPatrimonio + salary), bg:"#FEF3C7", c:"#F59E0B", ic:<Banknote size={16}/> },
        ].map((m,i) => (
          <div key={i} style={S.metric}>
            <div><div style={S.mLbl}>{m.l}</div><div style={S.mVal}>{m.v}</div></div>
            <div style={{ ...S.mIcon,background:m.bg,color:m.c }}>{m.ic}</div>
          </div>
        ))}
      </div>

      {/* Salary input */}
      <div style={S.card}>
        <div style={S.cardT}><DollarSign size={15}/> Salário Mensal</div>
        <div style={{ display:"flex",gap:12,alignItems:"center" }}>
          <span style={{ fontSize:13,color:"#6B7280",whiteSpace:"nowrap" }}>R$</span>
          <input
            type="number"
            value={salary || ""}
            onChange={e => setSalary(parseFloat(e.target.value)||0)}
            placeholder="0,00"
            style={{ ...S.input, maxWidth:220 }}
          />
        </div>
      </div>

      {/* Accounts */}
      <div style={S.card}>
        <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14 }}>
          <div style={S.cardT}>🏦 Contas Bancárias</div>
          <button style={S.btn1} onClick={() => {
            const newAcc = { id:`acc${Date.now()}`,name:"Nova Conta",bank:"",type:"checking",balance:0,color:"#6B7280",icon:"🏦" };
            setAccounts(p => [...p, newAcc]);
            setEditItem(newAcc); setModalOpen("editAccount");
          }}><Plus size={13}/> Adicionar</button>
        </div>
        {accounts.length === 0 ? (
          <div style={{ color:"#9CA3AF",fontSize:13,textAlign:"center",padding:20 }}>Nenhuma conta cadastrada</div>
        ) : (
          <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:12 }}>
            {accounts.map(a => (
              <div key={a.id} style={{ borderRadius:12,padding:14,border:`2px solid ${a.color}30`,background:`${a.color}08` }}>
                <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8 }}>
                  <div style={{ display:"flex",alignItems:"center",gap:8 }}>
                    <span style={{ fontSize:20 }}>{a.icon||"🏦"}</span>
                    <div>
                      <div style={{ fontSize:13,fontWeight:700 }}>{a.name}</div>
                      <div style={{ fontSize:11,color:"#9CA3AF" }}>{a.bank || (a.type==="checking"?"Corrente":a.type==="savings"?"Poupança":"Investimento")}</div>
                    </div>
                  </div>
                  <div style={{ display:"flex",gap:4 }}>
                    <button onClick={() => { setEditItem(a); setModalOpen("editAccount"); }} style={{ ...S.btnSm,background:"#F3F4F6",color:"#6B7280" }}><Edit3 size={11}/></button>
                    <button onClick={() => setAccounts(p => p.filter(x => x.id !== a.id))} style={S.btnDanger}><Trash2 size={11}/></button>
                  </div>
                </div>
                <div style={{ fontSize:20,fontWeight:800,color:a.color }}>{fmt(a.balance)}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Cards */}
      <div style={S.card}>
        <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14 }}>
          <div style={S.cardT}>💳 Cartões de Crédito</div>
          <button style={S.btn1} onClick={() => {
            const nc = { id:`card${Date.now()}`,name:"Novo Cartão",bank:"",limit:10000,used:0,closing:5,due:15,color:"#6B7280" };
            setCards(p => [...p, nc]);
            setEditItem(nc); setModalOpen("editCard");
          }}><Plus size={13}/> Adicionar</button>
        </div>
        {cards.length === 0 ? (
          <div style={{ color:"#9CA3AF",fontSize:13,textAlign:"center",padding:20 }}>Nenhum cartão cadastrado</div>
        ) : (
          <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))",gap:12 }}>
            {cards.map(c => {
              const pct = c.limit > 0 ? c.used / c.limit : 0;
              return (
                <div key={c.id} style={{ borderRadius:14,padding:16,background:`linear-gradient(135deg,${c.color},${c.color}CC)`,color:"#fff" }}>
                  <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12 }}>
                    <div>
                      <div style={{ fontSize:14,fontWeight:700 }}>💳 {c.name}</div>
                      {c.bank && <div style={{ fontSize:11,opacity:0.8 }}>{c.bank}</div>}
                    </div>
                    <div style={{ display:"flex",gap:4 }}>
                      <button onClick={() => { setEditItem(c); setModalOpen("editCard"); }} style={{ ...S.btnSm,background:"rgba(255,255,255,0.2)",color:"#fff",border:"none" }}><Edit3 size={11}/></button>
                      <button onClick={() => setCards(p => p.filter(x => x.id !== c.id))} style={{ ...S.btnSm,background:"rgba(255,255,255,0.15)",color:"#fff",border:"none" }}><Trash2 size={11}/></button>
                    </div>
                  </div>
                  <div style={{ display:"flex",justifyContent:"space-between",marginBottom:6 }}>
                    <div><div style={{ fontSize:10,opacity:0.8 }}>Usado</div><div style={{ fontSize:18,fontWeight:800 }}>{fmt(c.used)}</div></div>
                    <div style={{ textAlign:"right" }}><div style={{ fontSize:10,opacity:0.8 }}>Limite</div><div style={{ fontSize:18,fontWeight:800 }}>{fmt(c.limit)}</div></div>
                  </div>
                  <div style={{ height:5,background:"rgba(255,255,255,0.3)",borderRadius:3,overflow:"hidden",marginBottom:6 }}>
                    <div style={{ height:"100%",width:`${Math.min(pct*100,100)}%`,background:"#fff",borderRadius:3 }}/>
                  </div>
                  <div style={{ display:"flex",justifyContent:"space-between",fontSize:10,opacity:0.8 }}>
                    <span>Disponível: {fmt(c.limit - c.used)}</span>
                    <span>Fecha {c.closing} · Vence {c.due}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Investments */}
      <div style={S.card}>
        <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14 }}>
          <div style={S.cardT}><Briefcase size={15}/> Investimentos</div>
          <button style={S.btn1} onClick={() => setShowAssetForm(true)}><Plus size={13}/> Adicionar</button>
        </div>

        {showAssetForm && (
          <div style={{ background:"#F9FAFB",borderRadius:12,padding:14,marginBottom:14,border:"1px solid #E5E7EB" }}>
            <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10 }}>
              <input value={newAssetName} onChange={e => setNewAssetName(e.target.value)} placeholder="Nome do ativo" style={S.input}/>
              <select value={newAssetType} onChange={e => setNewAssetType(e.target.value)} style={{ ...S.sel,width:"100%" }}>
                {ASSET_TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10 }}>
              <input value={newAssetValue} onChange={e => setNewAssetValue(e.target.value)} placeholder="Valor (R$)" style={S.input} type="number"/>
              <input value={newAssetRate} onChange={e => setNewAssetRate(e.target.value)} placeholder="Rentabilidade (ex: 12%)" style={S.input}/>
            </div>
            <div style={{ display:"flex",gap:8 }}>
              <button style={{ ...S.btn2,flex:1,justifyContent:"center" }} onClick={() => setShowAssetForm(false)}>Cancelar</button>
              <button style={{ ...S.btn1,flex:1,justifyContent:"center" }} onClick={addAsset}><Check size={13}/> Adicionar</button>
            </div>
          </div>
        )}

        {assets.length === 0 ? (
          <div style={{ color:"#9CA3AF",fontSize:13,textAlign:"center",padding:20 }}>Nenhum investimento cadastrado</div>
        ) : (
          <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))",gap:16 }}>
            <div>
              {assets.map(a => (
                <div key={a.id} style={{ display:"flex",alignItems:"center",gap:10,padding:"10px 0",borderBottom:"1px solid #F9FAFB" }}>
                  <div style={{ width:10,height:10,borderRadius:"50%",background:a.color,flexShrink:0 }}/>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:13,fontWeight:600 }}>{a.name}</div>
                    <div style={{ fontSize:11,color:"#9CA3AF" }}>{a.type} · {a.rate}</div>
                  </div>
                  <div style={{ fontSize:13,fontWeight:700 }}>{fmt(a.value)}</div>
                  <button onClick={() => setAssets(p => p.filter(x => x.id !== a.id))} style={S.btnDanger}><Trash2 size={11}/></button>
                </div>
              ))}
              <div style={{ paddingTop:10,display:"flex",justifyContent:"flex-end",fontSize:14,fontWeight:700 }}>
                Total: {fmt(totalAssets)}
              </div>
            </div>
            {assetsByType.length > 1 && (
              <div style={{ height:220 }}>
                <div style={{ fontSize:12,fontWeight:600,color:"#6B7280",marginBottom:8 }}>Distribuição</div>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={assetsByType} cx="50%" cy="50%" innerRadius="50%" outerRadius="80%" dataKey="value" paddingAngle={2} stroke="none">
                      {assetsByType.map((d,i) => <Cell key={i} fill={d.color}/>)}
                    </Pie>
                    <Tooltip content={<DTT/>}/>
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   PAGE: ORÇAMENTO
   ══════════════════════════════════════════════════════════════ */
function OrcamentoPage({ budgets, setBudgets, budgetMonth, setBudgetMonth, budgetComparison, monthlyData, expCats, allCats, realTransactions }) {
  const fmt = v => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  const totalPlanned = budgetComparison.reduce((s, b) => s + b.planned, 0);
  const totalSpent = budgetComparison.reduce((s, b) => s + b.spent, 0);

  // editing mode state
  const [editing, setEditing] = useState(budgets.length === 0);
  // draft used while editing so user can cancel or save
  const [draft, setDraft] = useState(() => budgets.map(b => ({ ...b })));

  // sync draft when budgets change externally (e.g. first load)
  useEffect(() => {
    if (!editing) setDraft(budgets.map(b => ({ ...b })));
  }, [budgets, editing]);

  const handleSave = () => {
    setBudgets(draft);
    setEditing(false);
  };

  const handleEdit = () => {
    setDraft(budgets.map(b => ({ ...b })));
    setEditing(true);
  };

  const addRow = () => {
    const newCat = expCats.find(c => !draft.some(b => b.catId === c.id));
    if (newCat) setDraft(p => [...p, { catId: newCat.id, fixed: 0 }]);
  };

  // Month-to-month comparison
  const [compareCategory, setCompareCategory] = useState(expCats[0]?.id || "");
  const compareData = useMemo(() => {
    return monthlyData.slice(-6).map(m => {
      const catSpent = realTransactions.filter(t =>
        t.category === compareCategory &&
        t.date &&
        `${t.date.getFullYear()}-${String(t.date.getMonth()+1).padStart(2,"0")}` === m.key
      ).reduce((s,t) => s+t.value, 0);
      const budget = budgets.find(b => b.catId === compareCategory);
      const planned = budget?.fixed||0;
      return { name: m.name, Real: Math.round(catSpent), Orçado: planned };
    });
  }, [compareCategory, monthlyData, realTransactions, budgets]);

  return (
    <div>
      <div style={S.pageTitle}>Orçamento</div>
      <div style={S.pageDesc}>Planejamento mensal e comparação mês a mês</div>

      <div style={{ display:"flex",gap:10,alignItems:"center",marginBottom:16,flexWrap:"wrap" }}>
        <span style={{ fontSize:13,color:"#6B7280" }}>Mês:</span>
        <input type="month" value={budgetMonth} onChange={e => setBudgetMonth(e.target.value)} style={S.sel}/>
      </div>

      {/* VIEW MODE */}
      {!editing && (
        <>
          {budgets.length === 0 ? (
            <div style={{ ...S.card, textAlign:"center", padding:40 }}>
              <div style={{ fontSize:32, marginBottom:12 }}>🎯</div>
              <div style={{ color:"#6B7280", marginBottom:16 }}>Nenhum orçamento definido ainda</div>
              <button style={S.btn1} onClick={() => { setDraft([{ catId: expCats[0]?.id||"", fixed: 0 }]); setEditing(true); }}><Plus size={13}/> Criar Orçamento</button>
            </div>
          ) : (
            <>
              {/* KPIs */}
              <div style={S.metricRow}>
                {[
                  { l:"Total Orçado", v:fmt(totalPlanned), c:BRAND.green },
                  { l:"Gasto Real", v:fmt(totalSpent), c:totalSpent>totalPlanned?"#EF4444":"#22C55E" },
                  { l:"Diferença", v:fmt(Math.abs(totalPlanned - totalSpent)), c: totalSpent > totalPlanned ? "#EF4444" : "#F59E0B" },
                ].map((m,i) => (
                  <div key={i} style={S.metric}>
                    <div style={S.mLbl}>{m.l}</div>
                    <div style={{ ...S.mVal, fontSize:18, color:m.c }}>{m.v}</div>
                  </div>
                ))}
              </div>

              {/* Category bars */}
              <div style={S.card}>
                <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16 }}>
                  <div style={S.cardT}><Target size={15}/> Distribuição do Orçamento</div>
                  <button style={{ ...S.btn1, background:"#F3F4F6", color:"#374151" }} onClick={handleEdit}><Edit3 size={13}/> Editar</button>
                </div>
                {budgetComparison.map((b, idx) => {
                  const pct = totalPlanned > 0 ? b.planned / totalPlanned : 0;
                  const spentPct = b.planned > 0 ? Math.min(b.spent / b.planned, 1) : 0;
                  const over = b.planned > 0 && b.spent > b.planned;
                  return (
                    <div key={idx} style={{ marginBottom:14 }}>
                      <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4 }}>
                        <div style={{ display:"flex",alignItems:"center",gap:6,fontSize:13,fontWeight:600 }}>
                          <span>{b.cat?.icon}</span>
                          <span>{b.cat?.label}</span>
                        </div>
                        <div style={{ display:"flex",alignItems:"center",gap:10,fontSize:12 }}>
                          <span style={{ color:"#9CA3AF" }}>{fmt(b.spent)} / {fmt(b.planned)}</span>
                          <span style={{ fontWeight:700, minWidth:36, textAlign:"right", color: over?"#EF4444":"#6B7280" }}>
                            {(pct*100).toFixed(0)}%
                          </span>
                        </div>
                      </div>
                      <div style={{ height:7, background:"#F3F4F6", borderRadius:4, overflow:"hidden" }}>
                        <div style={{ height:"100%", width:`${spentPct*100}%`, background: over?"#EF4444":b.cat?.color||"#3B82F6", borderRadius:4, transition:"width 0.3s" }}/>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Budget vs real chart */}
              {budgetComparison.filter(b => b.planned > 0).length > 0 && (
                <div style={S.card}>
                  <div style={S.cardT}><Target size={15}/> Planejado vs Real</div>
                  <div style={{ height:260 }}>
                    <ResponsiveContainer>
                      <BarChart data={budgetComparison.filter(b=>b.planned>0).map(b => ({ name: b.cat?.label||"", Planejado: b.planned, Real: b.spent }))}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F3F4F6"/>
                        <XAxis dataKey="name" tick={{ fontSize:10,fill:"#9CA3AF" }} axisLine={false} tickLine={false}/>
                        <YAxis tick={{ fontSize:9,fill:"#9CA3AF" }} axisLine={false} tickLine={false} tickFormatter={v => v>=1000?`${(v/1000).toFixed(0)}k`:v}/>
                        <Tooltip content={<TT/>}/>
                        <Legend iconType="circle" iconSize={7} wrapperStyle={{ fontSize:11 }}/>
                        <Bar dataKey="Planejado" fill={BRAND.green} radius={[4,4,0,0]}/>
                        <Bar dataKey="Real" fill="#F59E0B" radius={[4,4,0,0]}/>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* EDIT MODE */}
      {editing && (
        <div style={S.card}>
          <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16 }}>
            <div style={S.cardT}>Editar Orçamento</div>
            <div style={{ display:"flex",gap:8 }}>
              {budgets.length > 0 && (
                <button style={{ ...S.btn1, background:"#F3F4F6", color:"#374151" }} onClick={() => { setDraft(budgets.map(b=>({...b}))); setEditing(false); }}>Cancelar</button>
              )}
              <button style={S.btn1} onClick={handleSave}><Check size={13}/> Salvar</button>
            </div>
          </div>

          {draft.map((b, idx) => {
            const cat = allCats.find(c => c.id === b.catId);
            return (
              <div key={idx} style={{ display:"flex",gap:10,alignItems:"center",marginBottom:10,flexWrap:"wrap" }}>
                <select value={b.catId} onChange={e => setDraft(p => p.map((x,i)=>i===idx?{...x,catId:e.target.value}:x))}
                  style={{ ...S.sel, flex:2, minWidth:140 }}>
                  {expCats.map(c => <option key={c.id} value={c.id}>{c.icon} {c.label}</option>)}
                </select>
                <div style={{ display:"flex",alignItems:"center",gap:6,flex:1,minWidth:120 }}>
                  <span style={{ fontSize:12,color:"#6B7280",whiteSpace:"nowrap" }}>R$</span>
                  <input type="number" value={b.fixed} min={0}
                    onChange={e => setDraft(p => p.map((x,i)=>i===idx?{...x,fixed:+e.target.value}:x))}
                    style={{ ...S.input, flex:1 }} placeholder="0"/>
                </div>
                <button onClick={() => setDraft(p => p.filter((_,i)=>i!==idx))} style={S.btnDanger}><Trash2 size={11}/></button>
              </div>
            );
          })}

          <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:14,paddingTop:14,borderTop:"1px solid #F3F4F6" }}>
            <button style={{ ...S.btn1, background:"#F3F4F6", color:"#374151" }} onClick={addRow}><Plus size={13}/> Categoria</button>
            <div style={{ fontSize:13,fontWeight:700,color:"#374151" }}>
              Total: {fmt(draft.reduce((s,b) => s+(b.fixed||0), 0))}
            </div>
          </div>
        </div>
      )}

      {/* Month to month comparison */}
      {!editing && budgets.length > 0 && (
        <div style={S.card}>
          <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,flexWrap:"wrap",gap:8 }}>
            <div style={S.cardT}>Comparativo Mês a Mês</div>
            <select value={compareCategory} onChange={e => setCompareCategory(e.target.value)} style={S.sel}>
              {expCats.map(c => <option key={c.id} value={c.id}>{c.icon} {c.label}</option>)}
            </select>
          </div>
          {compareData.some(d => d.Real > 0 || d.Orçado > 0) ? (
            <div style={{ height:220 }}>
              <ResponsiveContainer>
                <BarChart data={compareData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F3F4F6"/>
                  <XAxis dataKey="name" tick={{ fontSize:10,fill:"#9CA3AF" }} axisLine={false} tickLine={false}/>
                  <YAxis tick={{ fontSize:9,fill:"#9CA3AF" }} axisLine={false} tickLine={false} tickFormatter={v => v>=1000?`${(v/1000).toFixed(0)}k`:v}/>
                  <Tooltip content={<TT/>}/>
                  <Legend iconType="circle" iconSize={7} wrapperStyle={{ fontSize:11 }}/>
                  <Bar dataKey="Real" fill="#EF4444" radius={[4,4,0,0]}/>
                  <Bar dataKey="Orçado" fill={BRAND.green} radius={[4,4,0,0]}/>
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div style={{ color:"#9CA3AF",fontSize:13,padding:30,textAlign:"center" }}>
              Sem dados históricos para esta categoria ainda
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   PAGE: CATEGORIAS
   ══════════════════════════════════════════════════════════════ */
function CategoriasPage({ categories, setCategories, modalOpen, setModalOpen, editItem, setEditItem }) {
  const expCats = categories.filter(c => c.type === "expense");
  const incCats = categories.filter(c => c.type === "income");

  return (
    <div>
      <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16,flexWrap:"wrap",gap:10 }}>
        <div>
          <div style={S.pageTitle}>Categorias</div>
          <div style={S.pageDesc}>Personalize nomes, cores e emojis das categorias</div>
        </div>
        <button style={S.btn1} onClick={() => setModalOpen("newCat")}><Plus size={13}/> Nova Categoria</button>
      </div>

      <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))",gap:16 }}>
        <div style={S.card}>
          <div style={S.cardT}><span style={{ color:"#EF4444" }}>⬇</span> Despesas ({expCats.length})</div>
          {expCats.map(c => (
            <div key={c.id} style={{ display:"flex",alignItems:"center",gap:10,padding:"8px 0",borderBottom:"1px solid #F9FAFB" }}>
              <div style={{ width:32,height:32,borderRadius:8,background:c.color+"18",display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,flexShrink:0 }}>{c.icon}</div>
              <div style={{ flex:1,minWidth:0 }}>
                <span style={{ fontSize:13,fontWeight:600 }}>{c.label}</span>
              </div>
              <div style={{ width:12,height:12,borderRadius:3,background:c.color,flexShrink:0 }}/>
              <button onClick={() => { setEditItem(c); setModalOpen("editCat"); }} style={{ ...S.btnSm,background:"#EAF5EF",color:BRAND.green }}><Edit3 size={11}/></button>
              <button onClick={() => setCategories(p => p.filter(x => x.id !== c.id))} style={S.btnDanger}><Trash2 size={11}/></button>
            </div>
          ))}
        </div>

        <div style={S.card}>
          <div style={S.cardT}><span style={{ color:"#22C55E" }}>⬆</span> Receitas ({incCats.length})</div>
          {incCats.map(c => (
            <div key={c.id} style={{ display:"flex",alignItems:"center",gap:10,padding:"8px 0",borderBottom:"1px solid #F9FAFB" }}>
              <div style={{ width:32,height:32,borderRadius:8,background:c.color+"18",display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,flexShrink:0 }}>{c.icon}</div>
              <div style={{ flex:1,minWidth:0 }}>
                <span style={{ fontSize:13,fontWeight:600 }}>{c.label}</span>
              </div>
              <div style={{ width:12,height:12,borderRadius:3,background:c.color,flexShrink:0 }}/>
              <button onClick={() => { setEditItem(c); setModalOpen("editCat"); }} style={{ ...S.btnSm,background:"#EAF5EF",color:BRAND.green }}><Edit3 size={11}/></button>
              <button onClick={() => setCategories(p => p.filter(x => x.id !== c.id))} style={S.btnDanger}><Trash2 size={11}/></button>
            </div>
          ))}
        </div>
      </div>

      {/* Modals */}
      {modalOpen === "newCat" && (
        <NewCatModal
          onClose={() => setModalOpen(null)}
          onSave={(cat) => { setCategories(p => [...p, cat]); setModalOpen(null); }}
        />
      )}
      {modalOpen === "editCat" && editItem && (
        <EditCatModal
          cat={editItem}
          onClose={() => { setModalOpen(null); setEditItem(null); }}
          onSave={(updated) => { setCategories(p => p.map(c => c.id === updated.id ? updated : c)); setModalOpen(null); setEditItem(null); }}
        />
      )}
    </div>
  );
}
